import "../lib/readable_stream_polyfill"
import { clearAiDeepLink } from "../lib/ai_deep_link"
import { Controller } from "@hotwired/stimulus"

import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs"
import { extractPageHeaders, splitSpecBySections, splitDrawingBySheets } from "../pdf_splitter"
import { track } from "../lib/mixpanel"
import { showSheetPreview, hideSheetPreview } from "../lib/sheet_preview"

// workerSrc is set lazily in connect() from the Stimulus value

const ALLOWED_EXTENSIONS = ["jpg","jpeg","gif","png","dng","heic","heif","webp","tiff","bmp","doc","docx","pdf","xml","txt","xls","xlsx","xlsm","zip","rar","dwg","pptx","ppt"]
const MAX_UPLOAD_SIZE = 250 * 1024 * 1024

const CSI_DIVISIONS = {
  "01": "General Requirements",
  "02": "Existing Conditions",
  "03": "Concrete",
  "04": "Masonry",
  "05": "Metals",
  "06": "Wood, Plastics & Composites",
  "07": "Thermal & Moisture Protection",
  "08": "Openings",
  "09": "Finishes",
  "10": "Specialties",
  "11": "Equipment",
  "12": "Furnishings",
  "13": "Special Construction",
  "14": "Conveying Equipment",
  "21": "Fire Suppression",
  "22": "Plumbing",
  "23": "HVAC",
  "25": "Integrated Automation",
  "26": "Electrical",
  "27": "Communications",
  "28": "Electronic Safety & Security",
  "31": "Earthwork",
  "32": "Exterior Improvements",
  "33": "Utilities"
}

const DIVISION_COLORS = {
  "General Requirements": "bg-gray-100 text-gray-700",
  "Existing Conditions": "bg-stone-100 text-stone-700",
  "Concrete": "bg-blue-100 text-blue-700",
  "Masonry": "bg-orange-100 text-orange-700",
  "Metals": "bg-zinc-100 text-zinc-700",
  "Wood, Plastics & Composites": "bg-amber-100 text-amber-700",
  "Thermal & Moisture Protection": "bg-rose-100 text-rose-700",
  "Openings": "bg-sky-100 text-sky-700",
  "Finishes": "bg-purple-100 text-purple-700",
  "Specialties": "bg-indigo-100 text-indigo-700",
  "Equipment": "bg-emerald-100 text-emerald-700",
  "Furnishings": "bg-pink-100 text-pink-700",
  "Special Construction": "bg-fuchsia-100 text-fuchsia-700",
  "Conveying Equipment": "bg-violet-100 text-violet-700",
  "Fire Suppression": "bg-red-100 text-red-700",
  "Plumbing": "bg-cyan-100 text-cyan-700",
  "HVAC": "bg-teal-100 text-teal-700",
  "Integrated Automation": "bg-lime-100 text-lime-700",
  "Electrical": "bg-yellow-100 text-yellow-700",
  "Communications": "bg-slate-100 text-slate-700",
  "Electronic Safety & Security": "bg-red-100 text-red-600",
  "Earthwork": "bg-green-100 text-green-700",
  "Exterior Improvements": "bg-emerald-100 text-emerald-600",
  "Utilities": "bg-blue-100 text-blue-600"
}

const EMPTY_RESULT = { section_number: null, title: null, division: null }

// A protected/encrypted PDF references "/Encrypt" in its trailer. pdf-lib can't
// read those, so we detect it here and route the file through the server to be
// decrypted before splitting.
function pdfLooksEncrypted(arrayBuffer) {
  try {
    return new TextDecoder("latin1").decode(arrayBuffer).includes("/Encrypt")
  } catch (_) {
    return false
  }
}

// Extract text content from first N pages of a PDF using pdfjs
async function extractPdfText(arrayBuffer, maxPages = 5) {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  const pageCount = Math.min(pdf.numPages, maxPages)
  const pageTexts = []

  for (let i = 1; i <= pageCount; i++) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent()
    const text = content.items.map(item => item.str).join(" ")
    pageTexts.push(text)
  }

  return { pageTexts, totalPages: pdf.numPages }
}

// Render first N pages as JPEG images (fallback for scanned PDFs with no text)
async function renderPdfPagesToJpegs(arrayBuffer, maxPages = 5, targetWidth = 800) {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  const pageCount = Math.min(pdf.numPages, maxPages)
  const images = await Promise.all(
    Array.from({ length: pageCount }, async (_, i) => {
      const page = await pdf.getPage(i + 1)
      const viewport = page.getViewport({ scale: 1 })
      const scale = targetWidth / viewport.width
      const scaledViewport = page.getViewport({ scale })
      const canvas = document.createElement("canvas")
      canvas.width = scaledViewport.width
      canvas.height = scaledViewport.height
      const ctx = canvas.getContext("2d")
      ctx.fillStyle = "#fff"
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise
      const jpegBase64 = canvas.toDataURL("image/jpeg", 0.70).split(",")[1]
      canvas.width = canvas.height = 0
      return jpegBase64
    })
  )
  return { images, totalPages: pdf.numPages }
}

async function callExtractMetadata(url, mode, content, filename) {
  const token = document.querySelector('meta[name="csrf-token"]')?.content
  const body = { mode, filename }
  if (mode === "text") body.text = content
  else body.images = content

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    credentials: "same-origin",
    signal: AbortSignal.timeout(35000),
    body: JSON.stringify(body)
  })
  if (!res.ok) throw new Error(`extract_metadata HTTP ${res.status}`)
  return await res.json()
}

function detectDivision(sectionNumber) {
  if (!sectionNumber) return null
  const stripped = sectionNumber.trim().replace(/\s+/g, " ")
  const prefix = stripped.split(" ")[0].padStart(2, "0")
  return CSI_DIVISIONS[prefix] || null
}

// Pull a CSI section number from a filename when it's clearly there — our
// splitter names files "..._Section_017123.pdf", and specs are commonly named
// "01 71 23 Title.pdf". This is more reliable than re-running the AI (which can
// read the same page as a different number), so we prefer it when present.
function sectionFromFilename(name) {
  if (!name) return null
  let m = name.match(/section[_\s-]*(\d{6})(?:\D|$)/i)
  let digits = m && m[1]
  if (!digits) {
    m = name.match(/^\s*(\d{2})[\s.\-](\d{2})[\s.\-](\d{2})(?:\D|$)/)
    if (m) digits = m[1] + m[2] + m[3]
  }
  if (!digits) {
    m = name.match(/^\s*(\d{6})(?!\d)/) // bare leading number, but not part of a longer run (e.g. a date)
    if (m) digits = m[1]
  }
  return digits && digits.length === 6 ? digits.replace(/(\d{2})(\d{2})(\d{2})/, "$1 $2 $3") : null
}

export default class extends Controller {
  static targets = [
    "viewerPrevBtn", "viewerNextBtn",
    "tableContainer", "tableBody", "gridContainer", "sentinel", "empty", "skeleton",
    "searchInput", "searchClear", "aiFilterChip", "filterBadge", "clearFiltersRow", "datePresets", "dateFrom", "dateTo",
    "viewSwitcher", "divisionTabs", "divisionLabel", "divisionMenu", "showAllRevisionsToggle",
    "selectAllCheckbox", "selectAllCol", "bulkToggle",
    "detailModal", "detailBackdrop", "detailPanel", "detailContent", "detailLoading",
    "detailTitle", "detailPreview", "detailMeta", "detailDownloadBtn", "prevBtn", "nextBtn",
    "detailRevisionHistory", "detailLinkedItems",
    "uploadModal", "dropZone", "fileInput", "uploadSubmitBtn", "uploadMenu", "uploadMenuBtn",
    "uploadSetModeSelect",
    "uploadNewSetFields", "uploadExistingSetFields",
    "uploadSetNameInput", "uploadSetDateInput", "uploadSetSelect",
    "uploadSheetTable", "uploadSheetRows", "uploadAiStatus", "uploadAiStatusText",
    "bulkBar", "bulkCount", "compareBtn",
    "deleteModal", "deleteMessage", "wrongTypeModal", "wrongTypeLink",
    "moveModal", "moveMessage",
    "editModal", "editNumberInput", "editTitleInput", "editDivisionSelect", "editSetSelect", "editReceivedInput",
    "rowMenu",
    "specSetFilter",
    "exportModal", "exportDivisions", "exportSetSelect", "exportHistorical", "exportFileCount", "exportSubmitBtn", "exportCompiledBtn",
    "exportStaleWarning", "exportStaleText",
    "comparePickerModal", "comparePickerTitle", "comparePickerList", "comparePickerEmpty",
    "comparePickerPreviousWrap", "comparePickerOlderSection",
    "distributeModal", "distributeSearchInput", "distributeDropdownList", "distributeSendBtn",
    "distributeSelectAll", "distributeCount",
    "sortIconNumber", "sortIconTitle", "sortIconDivision", "sortIconRevision", "sortIconSet", "sortIconReceived",
    "bluebeamPopover", "bluebeamCopyLinkLabel", "bluebeamSessionId", "bluebeamSessionIdCopyLabel",
    "bluebeamFinishModal", "bluebeamVersionRadio",
    "bluebeamFinishNextRev", "bluebeamFinishCurrentRev", "bluebeamDiscardModal"
  ]

  static values = {
    projectId: Number,
    filesUrl: String,
    presignUrl: String,
    registerUrl: String,
    checkSectionsUrl: String,
    extractMetadataUrl: String,
    analyzeCombinedUrl: String,
    decryptUrl: String,
    drawingsPageUrl: String,
    drawingsRegisterUrl: String,
    drawingsCheckSheetsUrl: String,
    splitDocumentsUrl: String,
    referencesUrl: String,
    pdfWorkerUrl: String,
    specSetsUrl: String,
    saveViewModeUrl: String,
    saveFiltersUrl: String,
    distributeUrl: String,
    distributionMembersUrl: String,
    exportZipUrl: String,
    exportCompiledPdfUrl: String,
    compiledPdfStatusUrl: String,
    exportCountUrl: String,
    sectionHistoryUrlTemplate: String,
    savedFilters: { type: Object, default: {} },
    currentView: { type: String, default: "list" },
    canManageDocuments: { type: Boolean, default: false },
    bluebeamAllowed: { type: Boolean, default: false },
    builder: { type: Boolean, default: false },
    pdfViewerBaseUrl: String,
    bluebeamOpenUrlTemplate: String,
    bluebeamFinishUrlTemplate: String,
    bluebeamCloseUrlTemplate: String,
    bluebeamStatusUrlTemplate: String,
    copyMarkupsLabel: { type: String, default: "Copy the previous version's markups to this version" }
  }

  connect() {
    if (this.pdfWorkerUrlValue) pdfjsLib.GlobalWorkerOptions.workerSrc = this.pdfWorkerUrlValue
    this._page = 1
    this._loading = false
    this._isLastPage = false
    this._searchTimeout = null
    this._filters = {}
    this._fileIds = []
    this._filesData = {}
    this._activeFileId = null
    this._modalOpen = false
    this._selectedIds = new Set()
    this._bulkMode = false
    this._uploadFiles = []
    this._activeDivision = null
    this._statusFilter = "current"
    this._specSetFilter = null
    this._deleteTarget = null
    this._rowIndex = 0
    this._sort = { key: null, order: null }
    this._onKeydown = this._onKeydown.bind(this)
    this._specSetsCache = []
    this._bluebeamPollers = {}

    // "Markup in Bluebeam" button inside the PDF viewer
    this._onOpenBluebeam = (e) => {
      const id = e.detail?.pdfId
      if (id) this._triggerBluebeamOpen(id)
    }
    this.element.addEventListener("pdf-viewer:open-bluebeam", this._onOpenBluebeam)

    // Returning from the Bluebeam OAuth dance: a session is being created
    // for the doc in ?bluebeam_poll_doc — poll it and clean the URL.
    const bbPollDoc = new URLSearchParams(window.location.search).get("bluebeam_poll_doc")
    if (bbPollDoc) {
      this._pollBluebeamStatus(bbPollDoc)
      const cleanUrl = new URL(window.location.href)
      cleanUrl.searchParams.delete("bluebeam_poll_doc")
      window.history.replaceState({}, "", cleanUrl)
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this._loading && !this._isLastPage) {
          this._loadPage(this._page)
        }
      },
      { rootMargin: "300px", threshold: 0 }
    )

    // AI Radar deep link: ?ai_open=<id>&ai_page=<n>&ai_q=<section #> — filter
    // the list to the cited section and open it in the viewer at that page.
    // ai_hl is a verbatim quote to search & highlight inside the PDF (falls
    // back to the section number); ai_return is the action-item path to go
    // back to when the viewer is closed.
    const aiParams = new URLSearchParams(window.location.search)
    this._aiOpenId = aiParams.get("ai_open")
    this._aiOpenPage = parseInt(aiParams.get("ai_page"), 10) || 1
    const aiQ = aiParams.get("ai_q")
    this._aiOpenHl = (aiParams.get("ai_hl") || aiQ || "").trim() || null
    // ai_locate=<id>: locate-only deep link from ChatLog's Ask AI sources
    // (Phelipe 2026-08-24) — filter the list to the cited document and pulse
    // the row — without opening the PDF viewer.
    const aiLocate = aiParams.get("ai_locate")
    if (aiLocate && !this._aiOpenId) {
      this._aiLocateId = aiLocate
      this._aiSources = [{ id: aiLocate }]
    }
    const aiReturn = aiParams.get("ai_return")
    this._aiReturnUrl = (aiReturn && aiReturn.startsWith("/") && !aiReturn.startsWith("//")) ? aiReturn : null
    if (this._aiReturnUrl) {
      this._onPdfClosedReturn = () => {
        if (!this._aiReturnUrl) return
        const url = this._aiReturnUrl
        this._aiReturnUrl = null
        window.location.assign(url)
      }
      this.element.addEventListener("pdf-viewer:closed", this._onPdfClosedReturn)
    }
    // Read once, then out of the address — or Close reopened the sheet (Larry K7-25).
    clearAiDeepLink()

    this._restoreSavedFilters()
    if (aiQ) {
      this._filters.search = aiQ
      if (this.hasSearchInputTarget) this.searchInputTarget.value = aiQ
    }
    this._loadPage(1)

    if (this.canManageDocumentsValue) {
      this._setupPageDrag()
    }

    // "Show me" from the Ask-AI panel → filter the list to the answer's sources.
    this._onAiSources = (e) => this._applyAiSources(e.detail?.sources)
    window.addEventListener("docs-ai:show-sources", this._onAiSources)

    // Viewer open/close state + ArrowLeft/ArrowRight paging for the document
    // open in the Apryse viewer (standard drawings cycle their packet).
    this._viewerOpen = false
    this._onViewerClosed = () => { this._viewerOpen = false }
    this.element.addEventListener("pdf-viewer:closed", this._onViewerClosed)
    this._onViewerKeydown = this._onViewerKeydown.bind(this)
    document.addEventListener("keydown", this._onViewerKeydown)
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
    if (this._sdTimer) { clearTimeout(this._sdTimer); this._sdTimer = null }
    document.removeEventListener("keydown", this._onKeydown)
    document.removeEventListener("keydown", this._onViewerKeydown)
    window.removeEventListener("docs-ai:show-sources", this._onAiSources)
    this._teardownPageDrag()
    this.element.removeEventListener("pdf-viewer:open-bluebeam", this._onOpenBluebeam)
    this.element.removeEventListener("pdf-viewer:closed", this._onViewerClosed)
    if (this._onPdfClosedReturn) this.element.removeEventListener("pdf-viewer:closed", this._onPdfClosedReturn)
    Object.values(this._bluebeamPollers || {}).forEach(id => clearInterval(id))
    this._bluebeamPollers = {}
  }

  // ── Column Sorting ──────────────────────────────────────────────

  sortColumn(event) {
    const key = event.currentTarget.dataset.sortKey
    if (this._sort.key === key) {
      this._sort.order = this._sort.order === "asc" ? "desc" : "asc"
    } else {
      this._sort.key = key
      this._sort.order = "asc"
    }
    this._reRenderTable()
  }

  _reRenderTable() {
    this._rowIndex = 0
    const files = this._fileIds.map(id => this._filesData[id]).filter(Boolean)
    const sorted = this._sortFiles(files)
    this.tableBodyTarget.innerHTML = this._renderRowsTree(sorted)
    this._updateSortIcons()
    // Navigation order = what's on screen, top to bottom (mirrors the
    // Drawings-side fix from 2026-08-18 — the fetch order in _fileIds doesn't
    // match the grouped/sorted render). Synthetic group-header ids stay out.
    this._navIds = [...new Set([...this.tableBodyTarget.querySelectorAll("tr[data-file-id]")].map(r => r.dataset.fileId))].filter(id => /^\d+$/.test(id))
    // Rows just got replaced — a hover card anchored to an old row would
    // never receive its mouseleave.
    hideSheetPreview()
  }

  _sortFiles(items) {
    // Builder: the combined set original is always pinned on top, with
    // its extracted sections below it.
    const combinedFirst = (a, b) => (a.combined === b.combined ? 0 : a.combined ? -1 : 1)
    if (!this._sort.key) return [...items].sort(combinedFirst)
    const sorted = [...items]
    const dir = this._sort.order === "desc" ? -1 : 1
    sorted.sort((a, b) => {
      const cf = combinedFirst(a, b)
      if (cf !== 0) return cf
      let va, vb
      switch (this._sort.key) {
        case "number":
          // Natural compare: 201 < 202 < 1001 < 2010 (string compare would
          // wedge 2010 between 201 and 202).
          return (a.number || "").localeCompare(b.number || "", undefined, { numeric: true, sensitivity: "base" }) * dir
        case "title":
          va = (a.title || "").toLowerCase(); vb = (b.title || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "division":
          va = (a.division || "").toLowerCase(); vb = (b.division || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "revision":
          va = parseInt(a.revision_number) || 0; vb = parseInt(b.revision_number) || 0
          return (va - vb) * dir
        case "set":
          va = (a.spec_set_name || "").toLowerCase(); vb = (b.spec_set_name || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "received":
          va = a.received_date || a.formatted_date || ""
          vb = b.received_date || b.formatted_date || ""
          return va < vb ? -dir : va > vb ? dir : 0
        default: return 0
      }
    })
    return sorted
  }

  _updateSortIcons() {
    const arrowUp = `<svg class="w-3.5 h-3.5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5"/></svg>`
    const arrowDown = `<svg class="w-3.5 h-3.5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg>`
    const keys = { number: "Number", title: "Title", division: "Division", revision: "Revision", set: "Set", received: "Received" }
    for (const [key, name] of Object.entries(keys)) {
      const targetName = `sortIcon${name}Target`
      const hasName = `hasSortIcon${name}Target`
      if (this[hasName]) {
        this[targetName].innerHTML = this._sort.key === key ? (this._sort.order === "asc" ? arrowUp : arrowDown) : ""
      }
    }
  }

  // ── Search (300ms debounce) ────────────────────────────────────────

  search() {
    if (this._aiSources) this._clearAiState() // typing replaces the AI filter
    this._toggleSearchClear()
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      this._filters.search = this.searchInputTarget.value.trim()
      this._resetAndLoad()
      this._persistFilters()
    }, 300)
  }

  // "×" inside the search box — wipes the term and reloads the list.
  clearSearch() {
    clearTimeout(this._searchTimeout)
    this.searchInputTarget.value = ""
    this._filters.search = ""
    this._toggleSearchClear()
    this._resetAndLoad()
    this._persistFilters()
    this.searchInputTarget.focus()
  }

  // The × is only visible while there's something to clear.
  _toggleSearchClear() {
    if (this.hasSearchClearTarget) this.searchClearTarget.classList.toggle("hidden", !this.searchInputTarget.value)
  }

  // ── "Show me" (AI answer filter) ───────────────────────────────────
  // Filters the list to the documents an Ask-AI answer drew from, with the
  // answer's quotes as the expandable per-row snippets.

  _applyAiSources(sources) {
    const valid = (Array.isArray(sources) ? sources : []).filter(s => s && s.id)
    if (!valid.length) return
    this._aiSources = valid
    // The AI filter replaces any typed search — clear it so they don't fight.
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this._filters.search = ""
    this._toggleSearchClear()
    this._updateAiChip()
    this._resetAndLoad()
    track("AI Show Me Clicked", {
      project_id: this.projectIdValue,
      module: "specifications",
      documents: new Set(valid.map(s => String(s.id))).size
    })
  }

  clearAiFilter(e) {
    if (e) e.preventDefault()
    if (!this._aiSources) return
    this._clearAiState()
    this._resetAndLoad()
  }

  _clearAiState() {
    this._aiSources = null
    this._updateAiChip()
  }

  _updateAiChip() {
    if (!this.hasAiFilterChipTarget) return
    const on = !!this._aiSources
    this.aiFilterChipTarget.classList.toggle("hidden", !on)
    this.aiFilterChipTarget.classList.toggle("inline-flex", on)
    if (on) {
      const n = new Set(this._aiSources.map(s => String(s.id))).size
      const label = this.aiFilterChipTarget.querySelector("[data-ai-chip-label]")
      if (label) label.textContent = `AI answer · ${n} doc${n === 1 ? "" : "s"}`
    }
  }

  // Synthesize the per-row badge + snippet data from the AI's cited quotes,
  // reusing the search-results rendering as-is.
  _mergeAiMatches() {
    const map = {}
    this._aiSources.forEach(s => {
      const k = String(s.id)
      map[k] ||= { count: 0, pages: [], more_pages: 0 }
      map[k].count++
      map[k].pages.push({ page: s.page || 1, count: 1, snippet: s.quote || s.label || "", term: s.quote || "" })
    })
    this._contentMatches = map
  }

  // ── Date Filters ──────────────────────────────────────────────────

  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    const today = new Date()
    let dateFrom = ""

    if (preset) {
      const d = new Date(today)
      d.setDate(d.getDate() - parseInt(preset))
      dateFrom = d.toISOString().slice(0, 10)
    }

    this._filters.date_from = dateFrom
    this._filters.date_to = dateFrom ? today.toISOString().slice(0, 10) : ""

    if (this.hasDateFromTarget) this.dateFromTarget.value = this._filters.date_from
    if (this.hasDateToTarget) this.dateToTarget.value = this._filters.date_to

    this.datePresetsTargets.forEach(container => {
      container.querySelectorAll("button").forEach(btn => {
        if (btn.dataset.preset === preset) {
          btn.className = btn.className.replace(/text-gray-500 hover:bg-gray-50 border-gray-200/, "bg-brand/10 text-brand font-medium border-brand/30")
        } else {
          btn.className = btn.className.replace(/bg-brand\/10 text-brand font-medium border-brand\/30/, "text-gray-500 hover:bg-gray-50 border-gray-200")
        }
      })
    })

    this._resetAndLoad()
    this._persistFilters()
    this._updateFilterBadge()
  }

  filterByDate() {
    this._filters.date_from = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    this._filters.date_to = this.hasDateToTarget ? this.dateToTarget.value : ""

    this.datePresetsTargets.forEach(container => {
      container.querySelectorAll("button").forEach(btn => {
        btn.className = btn.className.replace(/bg-brand\/10 text-brand font-medium border-brand\/30/, "text-gray-500 hover:bg-gray-50 border-gray-200")
      })
    })

    this._resetAndLoad()
    this._persistFilters()
    this._updateFilterBadge()
  }

  filterBySpecSet() {
    this._specSetFilter = this.hasSpecSetFilterTarget ? this.specSetFilterTarget.value || null : null
    this._resetAndLoad()
    this._updateFilterBadge()
  }

  selectSpecSet(event) {
    event.preventDefault()
    const setId = event.currentTarget.dataset.setId || null
    this._specSetFilter = setId === "" ? null : setId
    this._populateSpecSetFilter(this._specSetsCache || [])
    this._resetAndLoad()
    this._updateFilterBadge()
  }

  clearFilters() {
    this._filters = {}
    this._specSetFilter = null
    this._clearAiState()
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this._toggleSearchClear()
    if (this.hasDateFromTarget) this.dateFromTarget.value = ""
    if (this.hasDateToTarget) this.dateToTarget.value = ""
    this._populateSpecSetFilter(this._specSetsCache || [])

    this.datePresetsTargets.forEach(container => {
      const buttons = container.querySelectorAll("button")
      buttons.forEach((btn, i) => {
        if (i === 0) {
          btn.className = btn.className.replace(/text-gray-500 hover:bg-gray-50 border-gray-200/, "bg-brand/10 text-brand font-medium border-brand/30")
        } else {
          btn.className = btn.className.replace(/bg-brand\/10 text-brand font-medium border-brand\/30/, "text-gray-500 hover:bg-gray-50 border-gray-200")
        }
      })
    })

    this._resetAndLoad()
    this._persistFilters()
    this._updateFilterBadge()
  }

  // ── View Toggle ───────────────────────────────────────────────────

  switchView(event) {
    const view = event.currentTarget.dataset.view
    if (view === this.currentViewValue) return
    this.currentViewValue = view

    this.viewSwitcherTarget.querySelectorAll("button").forEach(btn => {
      if (btn.dataset.view === view) {
        btn.classList.remove("text-text-muted", "font-medium", "hover:text-gray-700")
        btn.classList.add("text-brand", "font-bold")
      } else {
        btn.classList.remove("text-brand", "font-bold")
        btn.classList.add("text-text-muted", "font-medium", "hover:text-gray-700")
      }
    })

    if (view === "list") {
      this.tableContainerTarget.classList.remove("hidden")
      this.hasGridContainerTarget && this.gridContainerTarget.classList.add("hidden")
    } else {
      this.tableContainerTarget.classList.add("hidden")
      this.hasGridContainerTarget && this.gridContainerTarget.classList.remove("hidden")
    }

    this._resetAndLoad()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.saveViewModeUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      credentials: "same-origin",
      body: JSON.stringify({ mode: view })
    })
  }

  // ── Division Tabs ─────────────────────────────────────────────

  selectDivision(event) {
    event.preventDefault()
    const div = event.currentTarget.dataset.division || null
    this._activeDivision = div === "" ? null : div
    this._updateDivisionTabStyles()
    this._updateDivisionLabel()
    // Close the dropdown
    this.divisionMenuTarget.classList.add("hidden")
    this._resetAndLoad()
  }

  // ── Show All Revisions Toggle ───────────────────────────────────

  toggleAllRevisions(event) {
    this._statusFilter = event.currentTarget.checked ? "all" : "current"
    this._resetAndLoad()
  }

  // ── Selection ─────────────────────────────────────────────────────

  toggleSelectAll(event) {
    const checked = event.currentTarget.checked
    const checkboxes = this.element.querySelectorAll('[data-file-checkbox]')
    checkboxes.forEach(cb => {
      cb.checked = checked
      const id = cb.dataset.fileId
      if (checked) this._selectedIds.add(id)
      else this._selectedIds.delete(id)
    })
    this._updateBulkBar()
  }

  toggleFileSelect(event) {
    const id = event.currentTarget.dataset.fileId
    if (event.currentTarget.checked) {
      this._selectedIds.add(id)
    } else {
      this._selectedIds.delete(id)
    }
    if (this.hasSelectAllCheckboxTarget) {
      const total = this.element.querySelectorAll('[data-file-checkbox]').length
      this.selectAllCheckboxTarget.checked = this._selectedIds.size === total
      this.selectAllCheckboxTarget.indeterminate = this._selectedIds.size > 0 && this._selectedIds.size < total
    }
    this._updateBulkBar()
  }

  toggleBulkMode() {
    this._bulkMode = !this._bulkMode
    this._selectedIds.clear()

    // Toggle Select button vs bulk bar
    if (this.hasBulkToggleTarget) this.bulkToggleTarget.classList.toggle("hidden", this._bulkMode)
    if (this.hasBulkBarTarget) {
      this.bulkBarTarget.classList.toggle("hidden", !this._bulkMode)
      if (this._bulkMode) this.bulkBarTarget.classList.add("flex")
      else this.bulkBarTarget.classList.remove("flex")
    }

    // Toggle header checkbox column
    if (this.hasSelectAllColTarget) this.selectAllColTarget.classList.toggle("hidden", !this._bulkMode)
    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
    }

    // Keep column widths aligned: the table is fixed-layout sized by the
    // colgroup, so give the checkbox its own narrow column while bulk mode
    // is on (otherwise every column shifts and Section # goes extra-wide).
    const cg = this.hasTableContainerTarget ? this.tableContainerTarget.querySelector("colgroup") : null
    if (cg) {
      let cbCol = cg.querySelector("[data-cb-col]")
      if (this._bulkMode && !cbCol) {
        cbCol = document.createElement("col")
        cbCol.setAttribute("data-cb-col", "")
        cbCol.style.width = "40px"
        cg.insertBefore(cbCol, cg.children[1] || null)
      } else if (!this._bulkMode && cbCol) {
        cbCol.remove()
      }
    }

    // Toggle row checkboxes
    this.tableBodyTarget.querySelectorAll("[data-checkbox-col]").forEach(td => {
      td.classList.toggle("hidden", !this._bulkMode)
    })
    this.tableBodyTarget.querySelectorAll("[data-file-checkbox]").forEach(cb => {
      cb.checked = false
    })

    this._updateBulkBar()
  }

  deselectAll() {
    this._selectedIds.clear()
    this.element.querySelectorAll('[data-file-checkbox]').forEach(cb => cb.checked = false)
    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
    }
    this._updateBulkBar()
  }

  // ── Bulk Actions ──────────────────────────────────────────────────

  bulkDelete() {
    if (this._selectedIds.size === 0) return
    this._deleteTarget = { type: "bulk" }
    this.deleteMessageTarget.textContent = `Are you sure you want to delete ${this._selectedIds.size} file(s)?`
    this.deleteModalTarget.classList.remove("hidden")
  }

  // ── Detail Slide-Over ─────────────────────────────────────────────

  openFile(event) {
    if (event.target.closest('[data-file-checkbox]') || event.target.closest('[data-action-menu]')) return
    const id = event.currentTarget.dataset.fileId
    if (!id) return

    // Search-inside-documents: when this row matched the document's text, a
    // row click expands/collapses its snippet panel instead of opening the
    // file (the panel's items open the file at the matched page).
    if (event.currentTarget.tagName === "TR" && this._contentMatches?.[String(id)]) {
      this._toggleMatchRow(event.currentTarget, String(id))
      return
    }

    // Open PDF files in Apryse viewer, fall back to detail drawer for non-PDFs
    const file = this._filesData?.[String(id)]
    const ext = file?.file_name?.split(".")?.pop()?.toLowerCase()

    if (ext === "pdf") {
      this._openInViewer(id)
    } else {
      this._openDetail(id)
    }
  }

  // Open a document in the Apryse viewer. `extra` lets search results land on
  // the matched page with the term pre-searched ({ initialPage, searchTerm }).
  _openInViewer(id, extra = {}) {
    const file = this._filesData?.[String(id)]
    if (!file) return
    const dataUrl = this.pdfViewerBaseUrlValue.replace(/\/0\//, `/${id}/`)
    const viewer = this.element.querySelector("[data-controller='action-item-pdf-viewer']")
    const viewerCtrl = viewer && this.application.getControllerForElementAndIdentifier(viewer, "action-item-pdf-viewer")
    if (!viewerCtrl) return

    const saveUrl = dataUrl.replace("pdf_viewer_data", "save_pdf_annotations")
    const label = [file.number, file.title].filter(Boolean).join(" — ")
    const displayName = label ? `${label}  ·  ${file.file_name}` : file.file_name
    // Show the Compare button when this section has an older version (rev ≥ 2).
    const compareEnabled = this.canManageDocumentsValue && !file.combined && parseInt(file.revision_number, 10) >= 2
    const bluebeamEnabled = this.bluebeamAllowedValue && this.hasBluebeamOpenUrlTemplateValue && this._bluebeamEligible(file)
    // "Split" picker: standard drawings that apply to THIS section, plus
    // all specs and all drawings — minus the one already open. The
    // section number scopes the applicable standard drawings.
    const splitListUrl = this.hasSplitDocumentsUrlValue
      ? `${this.splitDocumentsUrlValue}?exclude=${id}&exclude_type=spec&section_number=${encodeURIComponent(file.number || "")}`
      : null
    // References strip: documents THIS section cites, detected from its text.
    const referencesUrl = this.hasReferencesUrlValue
      ? `${this.referencesUrlValue}?doc_id=${id}&doc_type=spec`
      : null
    viewerCtrl.open({ dataUrl, filename: displayName, punchlistId: id, saveUrl, compareEnabled, compareFileId: id, bluebeamEnabled, version: file.combined ? null : file.revision_number, versionCurrent: file.status === "current", splitListUrl, referencesUrl, ...extra })
    // Remember which doc is open so the viewer Prev/Next can page the list.
    this._setViewerNavState(id)
  }

  // Track the open viewer document + enable/disable the header chevrons
  // against its navigation list (packet siblings for an individual standard
  // drawing, the top-level list for everything else).
  _setViewerNavState(id) {
    this._viewerFileId = String(id)
    this._viewerOpen = true
    const nav = this._navIdsFor(id)
    const _vIdx = nav.indexOf(String(id))
    if (this.hasViewerPrevBtnTarget) this.viewerPrevBtnTarget.disabled = _vIdx <= 0
    if (this.hasViewerNextBtnTarget) this.viewerNextBtnTarget.disabled = _vIdx < 0 || _vIdx >= nav.length - 1
  }

  // Ordered ids for prev/next around this document. An individual standard
  // drawing cycles through its packet's sibling drawings, in the exact
  // numeric order the tree renders them (SD-301, SD-302, …); everything
  // else pages the top-level list as before.
  _navIdsFor(id) {
    const file = this._filesData?.[String(id)]
    if (file && this._isIndividualDrawing(file)) {
      const parent = this._filesData?.[String(file.combined_parent_id)]
      const siblings = parent && Array.isArray(parent.drawings) ? parent.drawings : []
      if (siblings.length) {
        return siblings.slice()
          .sort((a, b) => (parseInt(String(a.number || "").replace(/\D/g, ""), 10) || 0) - (parseInt(String(b.number || "").replace(/\D/g, ""), 10) || 0))
          .map(d => String(d.id))
      }
    }
    return (this._navIds && this._navIds.length) ? this._navIds : this._fileIds
  }

  // ArrowLeft/ArrowRight page the document open in the Apryse viewer —
  // only when the keystroke comes from the host page (not from inside the
  // viewer or a form field), so in-PDF navigation and typing stay untouched.
  _onViewerKeydown(event) {
    if (!this._viewerOpen || this._modalOpen) return
    // A side-by-side compare open on top of the viewer owns the keys.
    if (document.querySelector('[data-controller~="pdf-compare"]:not(.hidden)')) return
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    const t = event.target
    if (t && t.closest && t.closest('[data-controller~="action-item-pdf-viewer"]')) return
    if (t && (/^(input|textarea|select)$/i.test(t.tagName || "") || t.isContentEditable)) return
    event.preventDefault()
    if (event.key === "ArrowLeft") this.viewerPrev()
    else this.viewerNext()
  }

  // ── Search-inside-documents results ───────────────────────────────

  // Snippet click → open the PDF at that page with the term pre-searched.
  // AI-filter snippets carry their own phrase (data-term, from the quote).
  openFileAtPage(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    const page = parseInt(event.currentTarget.dataset.page, 10) || 1
    const term = event.currentTarget.dataset.term || this._filters.search || null
    this._openInViewer(id, { initialPage: page, searchTerm: term })
  }

  _toggleMatchRow(rowEl, id) {
    const existing = this.tableBodyTarget.querySelector(`[data-match-row-for="${id}"]`)
    const chev = rowEl.querySelector("[data-match-chevron]")
    if (existing) {
      existing.remove()
      chev?.classList.remove("rotate-180")
      return
    }
    const file = this._filesData?.[String(id)]
    const match = this._contentMatches?.[String(id)]
    if (!file || !match) return
    // Span only the VISIBLE columns — a display:none td (hidden checkbox col)
    // still counts as a child, and an oversized colspan makes the browser
    // reserve a phantom column of dead space on the right.
    const colCount = rowEl.querySelectorAll("td:not(.hidden)").length
    rowEl.insertAdjacentHTML("afterend", this._matchRowHtml(file, match, colCount))
    chev?.classList.add("rotate-180")
  }

  _matchRowHtml(file, match, colCount) {
    const term = this._filters.search || ""
    // AI-filter snippets open the viewer searching a short phrase from their
    // own quote (first 8 words) instead of the (empty) search-bar term.
    const termAttr = (p) => {
      if (!p.term) return ""
      const phrase = String(p.term).split(/\s+/).slice(0, 8).join(" ")
      return ` data-term="${this._escapeHtml(phrase).replace(/"/g, "&quot;")}"`
    }
    const items = (match.pages || []).map(p => `
      <button type="button" data-action="click->specifications#openFileAtPage" data-file-id="${file.id}" data-page="${p.page}"${termAttr(p)}
              class="w-full flex items-start gap-2.5 text-left rounded-lg border border-gray-200 bg-white hover:border-brand/40 hover:bg-brand/[0.03] px-3 py-2 cursor-pointer">
        <span class="shrink-0 inline-flex items-center rounded bg-gray-100 text-gray-600 px-1.5 py-px text-[13px] font-medium mt-px">p. ${p.page}</span>
        <span class="text-[13px] text-gray-700 leading-5 min-w-0 break-words">${this._highlightTerm(p.snippet, term)}</span>
        ${p.count > 1 ? `<span class="ml-auto shrink-0 text-[13px] text-gray-400">${p.count}×</span>` : ""}
      </button>`).join("")
    const more = match.more_pages > 0
      ? `<div class="text-[13px] text-gray-400 mt-1.5">+ ${match.more_pages} more page${match.more_pages === 1 ? "" : "s"} with matches — open the document to see all</div>`
      : ""
    // The w-0/min-w-full wrapper keeps this wide content from contributing to
    // the table's auto layout — otherwise a long snippet stretches the table
    // and every other row shows dead space on the right.
    return `<tr data-match-row-for="${file.id}" class="border-b border-gray-200 bg-brand/[0.02]">
      <td colspan="${colCount}" class="px-4 xl:px-6 py-3">
        <div class="w-0 min-w-full">
          <div class="flex items-center justify-between gap-3 mb-2">
            <span class="text-[13px] font-medium text-gray-700">${match.count} result${match.count === 1 ? "" : "s"} inside this document</span>
            <button type="button" data-action="click->specifications#openFileAtPage" data-file-id="${file.id}" data-page="${match.pages?.[0]?.page || 1}"
                    class="text-[13px] text-brand font-medium hover:underline cursor-pointer shrink-0">Open document →</button>
          </div>
          <div class="space-y-1.5">${items}</div>
          ${more}
        </div>
      </td>
    </tr>`
  }

  // Escape, then wrap each occurrence of the term in a highlight mark.
  _highlightTerm(text, term) {
    const esc = this._escapeHtml(text || "")
    if (!term) return esc
    const safe = this._escapeHtml(term).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    try {
      return esc.replace(new RegExp(safe, "gi"), m => `<mark class="bg-amber-200/80 text-gray-900 rounded-sm px-0.5">${m}</mark>`)
    } catch (_) {
      return esc
    }
  }

  closeDetail() {
    if (!this._modalOpen) return
    this._modalOpen = false
    this.detailBackdropTarget.classList.remove("opacity-100")
    this.detailBackdropTarget.classList.add("opacity-0")
    this.detailPanelTarget.classList.add("translate-x-full")
    setTimeout(() => {
      this.detailModalTarget.classList.add("hidden")
    }, 300)
    document.removeEventListener("keydown", this._onKeydown)
    this._activeFileId = null
  }

  backdropMousedown(event) {
    this._backdropMousedown = event.target === this.detailBackdropTarget
  }

  closeOnBackdrop(event) {
    if (this._backdropMousedown && event.target === this.detailBackdropTarget) {
      this.closeDetail()
    }
    this._backdropMousedown = false
  }

  prevFile() {
    const nav = this._navIdsFor(this._activeFileId)
    const idx = nav.indexOf(String(this._activeFileId))
    if (idx > 0) this._openDetail(nav[idx - 1])
  }

  nextFile() {
    const nav = this._navIdsFor(this._activeFileId)
    const idx = nav.indexOf(String(this._activeFileId))
    if (idx >= 0 && idx < nav.length - 1) this._openDetail(nav[idx + 1])
  }

  // Page the open PDF to the next/previous doc in the list without closing.
  // replaceTab: arrowing swaps the sheet inside the CURRENT tab (Larry
  // 2026-08-20 — hunting with next/prev used to stack a new tab per press).
  viewerPrev() {
    const nav = this._navIdsFor(this._viewerFileId)
    const idx = nav.indexOf(String(this._viewerFileId))
    if (idx > 0) this._openInViewer(nav[idx - 1], { replaceTab: true })
  }

  viewerNext() {
    const nav = this._navIdsFor(this._viewerFileId)
    const idx = nav.indexOf(String(this._viewerFileId))
    if (idx >= 0 && idx < nav.length - 1) this._openInViewer(nav[idx + 1], { replaceTab: true })
  }

  // Hover thumbnail on the sections list (Larry 2026-08-20).
  sheetPreviewShow(event) {
    const el = event.currentTarget
    showSheetPreview(el, el.dataset.pdfUrl, el.dataset.thumbLabel)
  }

  sheetPreviewHide() { hideSheetPreview() }

  // ── Delete ────────────────────────────────────────────────────────

  showDeleteConfirm() {
    this._deleteTarget = { type: "single", id: this._activeFileId }
    this.deleteMessageTarget.textContent = this._deleteMessageFor(this._activeFileId)
    this.deleteModalTarget.classList.remove("hidden")
  }

  // Deleting a combined book also deletes its extracted sections — say so.
  _deleteMessageFor(id) {
    const f = (this._filesData || {})[String(id)]
    if (f && f.combined) {
      const kids = Object.values(this._filesData || {}).filter(o => o && String(o.combined_parent_id) === String(id)).length
      if (kids > 0) return `This is a combined file — deleting it also deletes its ${kids} extracted section${kids === 1 ? "" : "s"}. Are you sure?`
    }
    return "Are you sure you want to delete this file?"
  }

  hideDeleteConfirm() {
    this.deleteModalTarget.classList.add("hidden")
    this._deleteTarget = null
  }

  async confirmDelete() {
    if (!this._deleteTarget) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      if (this._deleteTarget.type === "single") {
        const id = this._deleteTarget.id
        await fetch(`/projects/${this.projectIdValue}/specifications/${id}`, {
          method: "DELETE",
          headers: { "X-CSRF-Token": token, "Accept": "application/json" },
          credentials: "same-origin"
        })
        if (this._modalOpen && String(this._activeFileId) === String(id)) {
          this.closeDetail()
        }
        this._showToast("File deleted")
        // Reload so counts (breadcrumb, division tabs, sidebar) refresh and any
        // sections orphaned by deleting a combined parent re-render.
        this._resetAndLoad()
      } else if (this._deleteTarget.type === "bulk") {
        const ids = Array.from(this._selectedIds).map(Number)
        await fetch(`/projects/${this.projectIdValue}/specifications/bulk_destroy`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ ids })
        })
        this._selectedIds.clear()
        this._updateBulkBar()
        this._showToast(`${ids.length} file(s) deleted`)
        this._resetAndLoad()
      }
    } catch (error) {
      console.error("[Specifications] Delete failed:", error)
      this._showToast("Delete failed", "error")
    }

    this.hideDeleteConfirm()
  }

  // ── Wrong document type modal ─────────────────────────────────────

  _showWrongTypeModal() {
    if (this.hasWrongTypeLinkTarget) {
      this.wrongTypeLinkTarget.href = this.drawingsPageUrlValue
    }
    this.wrongTypeModalTarget.classList.remove("hidden")
  }

  hideWrongTypeModal() {
    this.wrongTypeModalTarget.classList.add("hidden")
  }

  // ── Upload Modal ──────────────────────────────────────────────────

  async openUploadModal() {
    this._uploadFiles = []
    this._splitStates = []
    if (this.hasUploadSheetRowsTarget) this.uploadSheetRowsTarget.innerHTML = ""
    if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.add("hidden")
    this.uploadSubmitBtnTarget.disabled = true
    this.uploadModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"

    // Reset set mode to "new"
    if (this.hasUploadSetModeSelectTarget) this.uploadSetModeSelectTarget.value = "new"
    if (this.hasUploadNewSetFieldsTarget) { this.uploadNewSetFieldsTarget.classList.remove("hidden"); this.uploadNewSetFieldsTarget.classList.add("flex") }
    if (this.hasUploadExistingSetFieldsTarget) { this.uploadExistingSetFieldsTarget.classList.add("hidden"); this.uploadExistingSetFieldsTarget.classList.remove("flex") }
    if (this.hasUploadSetNameInputTarget) this.uploadSetNameInputTarget.value = ""
    if (this.hasUploadSetDateInputTarget) this.uploadSetDateInputTarget.value = new Date().toISOString().slice(0, 10)

    // Fetch spec sets for "existing" dropdown
    await this._fetchSpecSets()
  }

  closeUploadModal() {
    this.uploadModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._uploadFiles = []
    this._splitCancelled = true
    clearInterval(this._aiTimerInterval)
    this.uploadAiStatusTarget.classList.add("hidden")
    this.uploadAiStatusTarget.classList.remove("flex")
  }

  toggleSetMode(event) {
    const mode = event.currentTarget.value
    if (mode === "new") {
      this.uploadNewSetFieldsTarget.classList.remove("hidden")
      this.uploadNewSetFieldsTarget.classList.add("flex")
      this.uploadExistingSetFieldsTarget.classList.add("hidden")
      this.uploadExistingSetFieldsTarget.classList.remove("flex")
    } else {
      this.uploadNewSetFieldsTarget.classList.add("hidden")
      this.uploadNewSetFieldsTarget.classList.remove("flex")
      this.uploadExistingSetFieldsTarget.classList.remove("hidden")
      this.uploadExistingSetFieldsTarget.classList.add("flex")
    }
  }

  clickDropZone() {
    this.fileInputTarget.click()
  }

  addFiles(event) {
    const files = event.target ? event.target.files : event
    if (!files || files.length === 0) return

    for (const file of files) {
      const ext = file.name.split(".").pop()?.toLowerCase()
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        this._showToast(`File type .${ext} is not allowed`, "error")
        continue
      }
      if (file.size > MAX_UPLOAD_SIZE) {
        this._showToast(`${file.name} exceeds 250MB limit`, "error")
        continue
      }
      const id = Date.now() + Math.round(Math.random() * 1000)
      this._uploadFiles.push({ id, file, sectionNumber: "", title: "", division: "" })
      if (ext !== "pdf") this._renderUploadSheetRow(id, file)
    }

    if (this.hasUploadSheetTableTarget && this.uploadSheetRowsTarget.children.length > 0) {
      this.uploadSheetTableTarget.classList.remove("hidden")
    }
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
    this._updateUploadBtnText()
    this._suggestSetName()
    if (event.target) event.target.value = ""

    // Keep button disabled while AI analyzes PDFs
    if (this._uploadFiles.some(uf => !uf._extracted && uf.file.name.split(".").pop()?.toLowerCase() === "pdf")) {
      this.uploadSubmitBtnTarget.disabled = true
      this.uploadSubmitBtnTarget.innerHTML = `<span class="inline-flex items-center gap-1.5"><svg class="w-3.5 h-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Extracting</span>`
    }

    // Check for combined PDFs first, then AI extraction for remaining
    this._checkForCombinedPdfs()
  }

  removeUploadFile(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    this._uploadFiles = this._uploadFiles.filter(f => f.id !== id)
    this._removeUploadRow(id)
    if (this._uploadFiles.length === 0 && this.hasUploadSheetTableTarget) {
      this.uploadSheetTableTarget.classList.add("hidden")
    }
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
    this._updateUploadBtnText()
  }

  uploadSectionNumberChanged(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (!uf) return
    uf.sectionNumber = event.currentTarget.value.trim()
    // Another section number: the box comes back on blur if that section has markups.
    this._showCopyMarkupsBox(id, null)

    // Auto-detect division
    const div = detectDivision(uf.sectionNumber)
    if (div) {
      uf.division = div
      const divInput = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"] [data-division-input]`)
      if (divInput) divInput.value = div
    }
  }

  async uploadSectionNumberBlur(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (!uf || !uf.sectionNumber) return

    // Check if section exists
    const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"]`)
    if (!row) return
    const sectionInput = event.currentTarget

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.checkSectionsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ section_numbers: [uf.sectionNumber] })
      })
      const results = await res.json()
      const result = results[0]

      // Same treatment as the auto-detected path: a bold REV/NEW tag next to
      // the file name so a manually-typed revision is just as obvious.
      const existingLabel = row.querySelector("[data-sheet-label]")
      if (existingLabel) existingLabel.remove()
      const fileNameSpan = row.querySelector("td:first-child span")

      if (result && result.exists) {
        sectionInput.title = `Revises ${result.section_number} (currently Rev ${result.current_revision || "?"})`
        sectionInput.classList.add("bg-amber-50", "border-amber-300")
        sectionInput.classList.remove("bg-blue-50", "border-blue-200")
        fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-amber-500 ml-1.5 whitespace-nowrap">REV</span>`)
        if (result.title && !uf.title) {
          uf.title = result.title
          const titleInput = row.querySelector('[data-title-input]')
          if (titleInput) titleInput.value = result.title
        }
      } else {
        sectionInput.title = "New section"
        sectionInput.classList.remove("bg-amber-50", "border-amber-300")
        fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-emerald-500 ml-1.5 whitespace-nowrap">NEW</span>`)
      }
      this._showCopyMarkupsBox(id, result)
    } catch (e) {
      console.error("[Specifications] check_sections failed:", e)
    }
  }

  uploadTitleChanged(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (uf) uf.title = event.currentTarget.value.trim()
  }

  uploadDivisionChanged(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (uf) uf.division = event.currentTarget.value
  }

  // Primary action + the two dropdown variants. mode: "both" (default) |
  // "combined" (just the combined original) | "extracted" (just the sections).
  submitUpload() { this._doUpload("both") }
  submitUploadCombinedOnly() { this._doUpload("combined") }
  submitUploadExtractedOnly() { this._doUpload("extracted") }

  toggleUploadMenu(event) {
    if (event) event.stopPropagation()
    if (this.hasUploadMenuTarget) this.uploadMenuTarget.classList.toggle("hidden")
  }

  _closeUploadMenu() {
    if (this.hasUploadMenuTarget) this.uploadMenuTarget.classList.add("hidden")
  }

  async _doUpload(mode = "both") {
    this._closeUploadMenu()
    let files = this._uploadFiles
    if (this.builderValue && this._uploadFiles.some(uf => uf._isCombined)) {
      if (mode === "combined") files = this._uploadFiles.filter(uf => uf._isCombined)
      else if (mode === "extracted") files = this._uploadFiles.filter(uf => !uf._isCombined)
    }
    if (files.length === 0) return
    this.uploadSubmitBtnTarget.disabled = true
    this.uploadSubmitBtnTarget.textContent = "Uploading..."
    // Disable the split-button's dropdown caret too while uploading.
    if (this.hasUploadMenuBtnTarget) this.uploadMenuBtnTarget.disabled = true

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      // 1. Get presigned URLs
      const fileMeta = files.map(uf => ({
        filename: uf.file.name,
        content_type: uf.file.type || "application/octet-stream",
        byte_size: uf.file.size
      }))

      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ files: fileMeta })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json()
        throw new Error(err.error || "Presign failed")
      }

      const { uploads } = await presignRes.json()

      // 2. Upload each file to S3
      const uploadPromises = uploads.map((upload, i) => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          const uf = files[i]
          const progressTrack = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"] [data-progress-track]`)
          const progressBar = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"] [data-progress-bar]`)
          if (progressTrack) { progressTrack.classList.remove("hidden"); progressTrack.classList.add("bg-gray-100") }

          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && progressBar) {
              const pct = Math.round((e.loaded / e.total) * 100)
              progressBar.style.width = `${pct}%`
            }
          }

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve({
                cache_name: upload.cache_name,
                filename: upload.filename,
                number: uf.sectionNumber,
                title: uf.title,
                division: uf.division,
                combined: uf._isCombined === true,
                standard_drawing: uf.standardDrawing === true,
                copy_markups: uf.copyMarkups === true,
                // S2-15: a drawing sheet split off an addendum → Drawings page.
                to_drawings: uf._toDrawings === true,
                sheet_number: uf.sheetNumber,
                discipline: uf.discipline
              })
            } else {
              reject(new Error(`Upload failed: ${xhr.status}`))
            }
          }
          xhr.onerror = () => reject(new Error("Upload error"))

          xhr.open("PUT", upload.upload_url)
          xhr.send(uf.file)
        })
      })

      const allResults = await Promise.all(uploadPromises)
      // S2-15: the specs register here; drawing sheets on the Drawings page.
      const results = allResults.filter(r => !r.to_drawings)
      const sheetResults = allResults.filter(r => r.to_drawings)

      // 3. Build register payload
      const isNewSet = this.hasUploadSetModeSelectTarget && this.uploadSetModeSelectTarget.value === "new"
      const registerPayload = { files: results }

      if (isNewSet) {
        registerPayload.spec_set_name = this.hasUploadSetNameInputTarget ? this.uploadSetNameInputTarget.value.trim() : ""
        registerPayload.spec_set_issued_date = this.hasUploadSetDateInputTarget ? this.uploadSetDateInputTarget.value : ""
      } else {
        registerPayload.spec_set_id = this.hasUploadSetSelectTarget ? this.uploadSetSelectTarget.value : ""
      }

      let regData = {}
      if (results.length > 0) {
        const regResp = await fetch(this.registerUrlValue, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin",
          body: JSON.stringify(registerPayload)
        })
        if (!regResp.ok) throw new Error("Registration failed")
        regData = await regResp.json()
      }
      const uploadedIds = (regData.files || []).map(f => f.id)

      // S2-15: file the addendum's drawing sheets on the Drawings page, in a
      // drawing set named like the spec set. A failure never undoes the specs.
      const sheetsFiled = sheetResults.length ? await this._fileDrawingSheets(sheetResults, token, isNewSet) : 0

      this.closeUploadModal()
      this._showToast(`${results.length} spec(s) uploaded` +
        (sheetsFiled ? ` · ${sheetsFiled} drawing sheet${sheetsFiled === 1 ? "" : "s"} filed in Drawings` : ""))

      // Standard-drawing packets appear instantly, but their individual
      // drawings are carved by a background job a minute or two later. Mark
      // those packets "pending" BEFORE rendering so each one shows an
      // "Extraction in Progress…" spinner on its row, then watch until the
      // drawings land and refresh the list automatically.
      const packetIds = (regData.files || []).filter(f => f.standard_drawing).map(f => f.id)
      this._sdPendingIds = new Set(packetIds.map(String))
      this._resetAndLoad()
      if (packetIds.length) this._watchStandardDrawings(packetIds)

      // Show distribution modal if we have uploaded file IDs
      if (uploadedIds.length > 0) {
        this._openDistributeModal(uploadedIds)
      }
    } catch (error) {
      console.error("[Specifications] Upload failed:", error)
      this._showToast(error.message || "Upload failed", "error")
    } finally {
      this.uploadSubmitBtnTarget.disabled = false
      if (this.hasUploadMenuBtnTarget) this.uploadMenuBtnTarget.disabled = false
      this._updateUploadBtnText()
    }
  }

  // ── Standard-drawing extraction watcher ──────────────────────────
  // After an upload that created standard-drawing packets, poll until their
  // individual drawings finish carving in the background, refreshing the list
  // in as they land. Shows a spinner banner so the wait is visible.
  _watchStandardDrawings(packetIds) {
    if (this._sdTimer) { clearTimeout(this._sdTimer); this._sdTimer = null }
    this._sdPendingIds = new Set(packetIds.map(String))
    let attempts = 0
    let lastTotal = -1
    let stable = 0
    const poll = async () => {
      // Bail if the user navigated away.
      if (!this.element || !document.body.contains(this.element)) return
      attempts++
      let data = null
      try {
        const u = new URL(`/projects/${this.projectIdValue}/specifications/extraction_status`, window.location.origin)
        packetIds.forEach(id => u.searchParams.append("packet_ids[]", id))
        const r = await fetch(u, { headers: { Accept: "application/json" } })
        if (r.ok) data = await r.json()
      } catch (e) { /* transient — keep polling */ }

      if (data) {
        // Track which specific packets are still being carved so each row can
        // spin (or stop) independently.
        this._sdPendingIds = new Set((data.pending_ids || []).map(String))
        if (data.total_drawings !== lastTotal) {
          // A packet finished — pull its drawings into the list and re-render
          // (finished packets show their drawings + drop their spinner; the
          // rest keep spinning).
          lastTotal = data.total_drawings
          stable = 0
          this._resetAndLoad()
        } else {
          stable++
        }
        // Extraction sits at 0 drawings for up to a minute or two while Gemini
        // reads each packet, so DON'T treat "no change yet" as finished — that
        // would kill the watch and clear the spinners while it's still working.
        // Stop only when: every packet is done; OR results landed and then
        // settled (~24s no change); OR nothing extracted after ~3 min
        // (empty/failed); OR the ~6 min hard cap.
        const settled = data.total_drawings > 0 && stable >= 3
        const gaveUp  = data.total_drawings === 0 && attempts >= 22
        if (data.done || settled || gaveUp || attempts >= 45) {
          this._sdFinishWatch(data.total_drawings)
          return
        }
      } else if (attempts >= 45) {
        this._sdFinishWatch(lastTotal < 0 ? 0 : lastTotal)
        return
      }
      this._sdTimer = setTimeout(poll, 8000)
    }
    this._sdTimer = setTimeout(poll, 8000)
  }

  // Stop watching: clear the timer, drop ALL spinners (so a genuinely-empty
  // packet — e.g. a cover sheet with no drawings — stops spinning once the
  // background work has settled), re-render, and toast the result.
  _sdFinishWatch(total) {
    if (this._sdTimer) { clearTimeout(this._sdTimer); this._sdTimer = null }
    const hadPending = this._sdPendingIds && this._sdPendingIds.size > 0
    this._sdPendingIds = new Set()
    if (hadPending) this._resetAndLoad()
    if (total != null && total > 0) {
      this._showToast(`${total} standard drawing${total === 1 ? "" : "s"} extracted.`)
    }
  }

  // ── Drop Zone Events ──────────────────────────────────────────────

  dragOverZone(event) {
    event.preventDefault()
    event.currentTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragEnterZone(event) {
    event.preventDefault()
    event.currentTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragLeaveZone(event) {
    event.currentTarget.classList.remove("border-brand", "bg-brand/5")
  }

  dropOnZone(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("border-brand", "bg-brand/5")
    this.addFiles(event.dataTransfer.files)
  }

  // ── Page-Level Drag ───────────────────────────────────────────────

  _isUploadModalOpen() {
    return this.hasUploadModalTarget && !this.uploadModalTarget.classList.contains("hidden")
  }

  _setupPageDrag() {
    this._onDragOver = (e) => { e.preventDefault() }
    this._onDrop = (e) => {
      e.preventDefault()
      if (this._isUploadModalOpen()) return
      if (e.dataTransfer?.files?.length > 0) {
        this.openUploadModal()
        setTimeout(() => this.addFiles(e.dataTransfer.files), 100)
      }
    }

    document.addEventListener("dragover", this._onDragOver)
    document.addEventListener("drop", this._onDrop)
  }

  _teardownPageDrag() {
    if (this._onDragOver) document.removeEventListener("dragover", this._onDragOver)
    if (this._onDrop) document.removeEventListener("drop", this._onDrop)
  }

  // ── Export ZIP ────────────────────────────────────────────────────

  openExportModal() {
    if (!this._divisionCounts) return

    // Populate division checkboxes
    const divisions = this._divisionCounts.filter(c => c.id !== null && c.count > 0)
    if (divisions.length > 0) {
      this.exportDivisionsTarget.innerHTML = divisions.map(div =>
        `<label class="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" class="form-checkbox rounded" style="accent-color: var(--color-brand)"
                 value="${this._escapeHtml(div.id)}" checked data-export-division>
          <span class="text-sm text-gray-700">${this._escapeHtml(div.name)} <span class="text-gray-400">(${div.count})</span></span>
        </label>`
      ).join("")
    } else {
      this.exportDivisionsTarget.innerHTML = '<p class="text-sm text-gray-400">No divisions found</p>'
    }

    // Populate spec set dropdown (only sets that still have files)
    const sets = this._setsWithFiles(this._specSetsCache)
    this.exportSetSelectTarget.innerHTML = `<option value="">All Sets</option>` +
      sets.map(s => `<option value="${s.id}">${this._escapeHtml(s.name)}</option>`).join("")

    // Reset historical checkbox
    this.exportHistoricalTarget.checked = false

    // Update file count
    this._updateExportFileCount()

    // Listen for changes
    this.exportDivisionsTarget.addEventListener("change", this._updateExportFileCount.bind(this))
    this.exportSetSelectTarget.addEventListener("change", this._updateExportFileCount.bind(this))
    this.exportHistoricalTarget.addEventListener("change", this._updateExportFileCount.bind(this))

    this.exportModalTarget.classList.remove("hidden")
  }

  hideExportModal() {
    this.exportModalTarget.classList.add("hidden")
  }

  async submitExport() {
    const divisions = [...this.exportDivisionsTarget.querySelectorAll("[data-export-division]:checked")]
      .map(cb => cb.value)
    const specSetId = this.exportSetSelectTarget.value
    const includeHistorical = this.exportHistoricalTarget.checked

    const body = new URLSearchParams()
    if (divisions.length > 0) {
      divisions.forEach(d => body.append("divisions[]", d))
    }
    if (specSetId) body.set("spec_set_id", specSetId)
    if (includeHistorical) body.set("include_historical", "true")

    this.exportSubmitBtnTarget.disabled = true
    this.exportSubmitBtnTarget.textContent = "Exporting..."

    try {
      const resp = await fetch(this.exportZipUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body
      })

      if (resp.ok) {
        this.hideExportModal()
        this._showToast("Your export is being prepared. You will receive an email with the download link.")
      } else {
        this._showToast("Export failed. Please try again.", "error")
      }
    } catch {
      this._showToast("Export failed. Please try again.", "error")
    } finally {
      this.exportSubmitBtnTarget.disabled = false
      this.exportSubmitBtnTarget.textContent = "Export ZIP"
    }
  }

  // Ask the server for the real export count + stale-set check for the
  // current selection (set + divisions + historical). Debounced so rapid
  // checkbox toggles don't fire a request each. Falls back to the cached
  // division counts if the request fails.
  _updateExportFileCount() {
    if (!this.hasExportFileCountTarget) return
    clearTimeout(this._exportCountTimeout)
    this._exportCountTimeout = setTimeout(() => this._fetchExportCount(), 150)
  }

  async _fetchExportCount() {
    const divisions = [...this.exportDivisionsTarget.querySelectorAll("[data-export-division]:checked")].map(cb => cb.value)
    const specSetId = this.exportSetSelectTarget?.value || ""
    const includeHistorical = this.exportHistoricalTarget?.checked || false

    if (!this.exportCountUrlValue) return this._renderExportCountFallback(divisions)

    const params = new URLSearchParams()
    divisions.forEach(d => params.append("divisions[]", d))
    if (specSetId) params.set("spec_set_id", specSetId)
    if (includeHistorical) params.set("include_historical", "true")

    // Token guards against an out-of-order response overwriting a newer one.
    const token = (this._exportCountToken = (this._exportCountToken || 0) + 1)
    try {
      const resp = await fetch(`${this.exportCountUrlValue}?${params.toString()}`, {
        headers: { Accept: "application/json" }, credentials: "same-origin"
      })
      const data = await resp.json()
      if (token !== this._exportCountToken) return
      this._renderExportCount(data.count || 0, data.stale || 0)
    } catch (_) {
      this._renderExportCountFallback(divisions)
    }
  }

  _renderExportCount(count, stale) {
    this.exportFileCountTarget.textContent = `${count} file${count !== 1 ? "s" : ""} selected`
    if (this.hasExportStaleWarningTarget) {
      const show = stale > 0
      this.exportStaleWarningTarget.classList.toggle("hidden", !show)
      if (show && this.hasExportStaleTextTarget) {
        this.exportStaleTextTarget.textContent =
          `${stale} section${stale !== 1 ? "s have" : " has"} a newer revision in another set — choose “All Sets” to include the latest.`
      }
    }
  }

  // Offline fallback: approximate from the cached current-revision counts.
  _renderExportCountFallback(divisions) {
    if (!this._divisionCounts) return
    const all = this._divisionCounts.filter(c => c.id !== null && c.count > 0)
    let count = 0
    if (divisions.length === 0 || divisions.length === all.length) {
      count = (this._divisionCounts.find(c => c.id === null)?.count) || 0
    } else {
      divisions.forEach(d => { count += (this._divisionCounts.find(c => c.id === d)?.count) || 0 })
    }
    this._renderExportCount(count, 0)
  }

  // ── Export Compiled PDF ───────────────────────────────────────────

  async submitCompiledPdf() {
    const divisions = [...this.exportDivisionsTarget.querySelectorAll("[data-export-division]:checked")]
      .map(cb => cb.value)
    const specSetId = this.exportSetSelectTarget.value
    const includeHistorical = this.exportHistoricalTarget.checked

    const body = new URLSearchParams()
    if (divisions.length > 0) {
      divisions.forEach(d => body.append("divisions[]", d))
    }
    if (specSetId) body.set("spec_set_id", specSetId)
    if (includeHistorical) body.set("include_historical", "true")

    this.exportCompiledBtnTarget.disabled = true
    this.exportSubmitBtnTarget.disabled = true
    this.exportCompiledBtnTarget.textContent = "Compiling..."

    try {
      const resp = await fetch(this.exportCompiledPdfUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body
      })

      if (!resp.ok) {
        this._showToast("Export failed. Please try again.", "error")
        return
      }

      const { token } = await resp.json()
      this.hideExportModal()
      this._showCompilingToast("Compiling specifications into a single PDF...")
      this._pollCompiledPdf(token)
    } catch {
      this._showToast("Export failed. Please try again.", "error")
    } finally {
      this.exportCompiledBtnTarget.disabled = false
      this.exportSubmitBtnTarget.disabled = false
      this.exportCompiledBtnTarget.textContent = "Export Compiled PDF"
    }
  }

  async _pollCompiledPdf(token, attempts = 0) {
    if (attempts > 60) {
      this._hideCompilingToast()
      this._showToast("PDF compilation is taking longer than expected. Please try again.", "error")
      return
    }

    await new Promise(r => setTimeout(r, 2000))

    try {
      const resp = await fetch(`${this.compiledPdfStatusUrlValue}?token=${token}`)
      const data = await resp.json()

      if (data.status === "processing") {
        this._pollCompiledPdf(token, attempts + 1)
      } else if (data.status === "ready") {
        this._hideCompilingToast()
        this._showToast("Compiled PDF ready — downloading now.")
        const blob = await fetch(data.url).then(r => r.blob())
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = data.filename || "specifications-compiled.pdf"
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      } else {
        this._hideCompilingToast()
        this._showToast(data.message || "PDF compilation failed.", "error")
      }
    } catch {
      this._hideCompilingToast()
      this._showToast("Failed to check compilation status.", "error")
    }
  }

  _showCompilingToast(message) {
    this._hideCompilingToast()
    const el = document.createElement("div")
    el.id = "compiled-pdf-generating-toast"
    el.className = "flash-toast-info"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "0")
    el.setAttribute("role", "status")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>`
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _hideCompilingToast() {
    const existing = document.getElementById("compiled-pdf-generating-toast")
    if (existing) existing.remove()
  }

  // ── Row Menu ─────────────────────────────────────────────────────

  toggleRowMenu(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const menu = btn.nextElementSibling

    // Close any other open menus
    this.rowMenuTargets.forEach(m => { if (m !== menu) m.classList.add("hidden") })

    menu.classList.toggle("hidden")

    // Position fixed menu — below the button when space allows, above otherwise.
    // Reserves bottom margin so the Intercom bubble (production) doesn't cover it.
    if (!menu.classList.contains("hidden")) {
      const rect = btn.getBoundingClientRect()
      const menuHeight = menu.offsetHeight
      const gap = 4
      const intercomBuffer = 96
      const spaceBelow = window.innerHeight - rect.bottom - intercomBuffer
      const spaceAbove = rect.top

      const flipUp = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow
      const top = flipUp
        ? Math.max(8, rect.top - menuHeight - gap)
        : rect.bottom + gap
      menu.style.top = `${top}px`
      menu.style.left = `${rect.right - menu.offsetWidth}px`

      const close = (e) => {
        if (!menu.contains(e.target) && e.target !== btn) {
          menu.classList.add("hidden")
          document.removeEventListener("click", close)
        }
      }
      document.addEventListener("click", close)
    }
  }

  // ── Row-level actions ─────────────────────────────────────────────

  // ── Move to Drawings (Phelipe 2026-09-14) ────────────────────────
  // The server re-runs the same sort-and-split an upload gets on the other
  // page, so the file arrives organized there; the rows here go to the trash.

  rowMove(event) {
    event.stopPropagation()
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    const id = event.currentTarget.dataset.fileId
    const file = this._filesData[String(id)]
    if (!file) return
    this._moveTarget = id
    if (this.hasMoveMessageTarget) this.moveMessageTarget.textContent = this._moveMessageFor(file)
    if (this.hasMoveModalTarget) this.moveModalTarget.classList.remove("hidden")
  }

  hideMoveConfirm() {
    if (this.hasMoveModalTarget) this.moveModalTarget.classList.add("hidden")
    this._moveTarget = null
  }

  async confirmMove() {
    const id = this._moveTarget
    if (!id) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(`/projects/${this.projectIdValue}/specifications/__ID__/move_to_drawings`.replace("__ID__", id), {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Accept": "application/json" },
        credentials: "same-origin"
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.message || `HTTP ${resp.status}`)
      if (this._modalOpen && String(this._activeFileId) === String(id)) this.closeDetail()
      this._showToast(data.split ? "Moved to Drawings and split into sheets" : "Moved to Drawings")
      this._resetAndLoad()
    } catch (error) {
      console.error("[specifications] Move failed:", error)
      this._showToast(error.message || "Move failed", "error")
    }
    this.hideMoveConfirm()
  }

  _moveMessageFor(file) {
    const name = file.title || file.file_name || "this file"
    const pack = file.combined ? " and everything cut from it" : ""
    return `Move "${name}"${pack} to the Drawings page? It will be organized there like an upload.`
  }

  rowDelete(event) {
    event.stopPropagation()
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    const id = event.currentTarget.dataset.fileId
    this._deleteTarget = { type: "single", id }
    this.deleteMessageTarget.textContent = this._deleteMessageFor(id)
    this.deleteModalTarget.classList.remove("hidden")
  }

  // True when this row is an individual drawing already split OUT of a
  // packet (its parent is itself a standard-drawing packet) — those don't
  // get an "Extract drawings" action.
  _isIndividualDrawing(file) {
    if (!file.combined_parent_id) return false
    const parent = this._filesData?.[String(file.combined_parent_id)]
    return !!(parent && parent.standard_drawing)
  }

  // Kick off the background split of a standard-drawing packet into its
  // individual drawings, then refresh the list once it's had time to run.
  async extractDrawings(event) {
    event.stopPropagation()
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    const id = event.currentTarget.dataset.fileId
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(`/projects/${this.projectIdValue}/specifications/${id}/extract_drawings`, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!resp.ok) throw new Error("request failed")
      this._showToast("Splitting this packet into individual drawings — they'll appear here shortly.")
      setTimeout(() => this._resetAndLoad(), 18000)
    } catch (e) {
      this._showToast("Couldn't start the split — please try again.", "error")
    }
  }

  // ── Edit ────────────────────────────────────────────────────────

  rowEdit(event) {
    event.stopPropagation()
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    const id = event.currentTarget.dataset.fileId
    const file = this._filesData[String(id)]
    if (!file) return

    this._editFileId = id
    this.editNumberInputTarget.value = file.number || ""
    this.editTitleInputTarget.value = file.title || ""
    this.editDivisionSelectTarget.value = file.division || ""
    const fp = this.editReceivedInputTarget._flatpickr
    if (fp) { fp.setDate(file.received_date || "", true) }
    else { this.editReceivedInputTarget.value = file.received_date || "" }

    // Populate set dropdown from cached spec sets
    const sets = this._specSetsCache || []
    this.editSetSelectTarget.innerHTML = sets.map(s =>
      `<option value="${s.id}" ${String(s.id) === String(file.spec_set_id) ? "selected" : ""}>${this._escapeHtml(s.name)}</option>`
    ).join("")

    this.editModalTarget.classList.remove("hidden")
  }

  hideEditModal() {
    this.editModalTarget.classList.add("hidden")
    this._editFileId = null
  }

  async confirmEdit() {
    if (!this._editFileId) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const id = this._editFileId

    try {
      const res = await fetch(`/projects/${this.projectIdValue}/specifications/${id}/update_section`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          number: this.editNumberInputTarget.value.trim(),
          title: this.editTitleInputTarget.value.trim(),
          division: this.editDivisionSelectTarget.value,
          spec_set_id: this.editSetSelectTarget.value,
          received_date: this.editReceivedInputTarget.value
        })
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()

      if (data.file) {
        this._filesData[String(id)] = data.file
        const oldRow = this.tableBodyTarget.querySelector(`tr[data-file-id="${id}"]`)
        if (oldRow) oldRow.outerHTML = this._renderRow(data.file)
        if (this.hasGridContainerTarget) {
          const oldCard = this.gridContainerTarget.querySelector(`[data-file-id="${id}"]`)
          if (oldCard) oldCard.outerHTML = this._renderCard(data.file)
        }
        if (this._modalOpen && String(this._activeFileId) === String(id)) {
          this._renderDetailContent(data.file)
        }
      }

      this._showToast("File updated")
    } catch (error) {
      console.error("[Specifications] Edit failed:", error)
      this._showToast("Update failed", "error")
    }

    this.hideEditModal()
  }

  // ── PDF Viewer ────────────────────────────────────────────────────

  openPdfViewer(event) {
    const fileId = event.currentTarget.dataset.fileId
    const file = this._filesData[String(fileId)]
    if (!file) return

    const pdfController = this.element.querySelector('[data-controller~="action-item-pdf-viewer"]')
    if (!pdfController) return

    const ctrl = this.application.getControllerForElementAndIdentifier(pdfController, "action-item-pdf-viewer")
    if (ctrl) {
      const dataUrl = this.pdfViewerBaseUrlValue.replace(/\/0\//, `/${fileId}/`)
      const isPdf = file.is_pdf || (file.file_name || "").toLowerCase().endsWith(".pdf")
      const compareEnabled = this.canManageDocumentsValue && isPdf && !file.combined && parseInt(file.revision_number, 10) >= 2
      const bluebeamEnabled = this.bluebeamAllowedValue && this.hasBluebeamOpenUrlTemplateValue && this._bluebeamEligible(file)
      ctrl.open({ dataUrl, filename: file.file_name, punchlistId: fileId, compareFileId: fileId, compareEnabled, bluebeamEnabled, version: file.combined ? null : file.revision_number, versionCurrent: file.status === "current", related: this._relatedStdDrawing(file) })
      this._setViewerNavState(fileId)
    }
  }

  // ── Private: Data Loading ─────────────────────────────────────────

  async _loadPage(page) {
    if (this._loading || this._isLastPage) return
    this._loading = true
    const seq = (this._loadSeq ||= 1)

    try {
      const url = new URL(this.filesUrlValue, window.location.origin)
      url.searchParams.set("page", page)
      url.searchParams.set("status", this._statusFilter)

      if (this._filters.search && !this._aiSources) url.searchParams.set("search", this._filters.search)
      if (this._filters.date_from) url.searchParams.set("date_from", this._filters.date_from)
      if (this._filters.date_to) url.searchParams.set("date_to", this._filters.date_to)
      if (this._filters.uploader_id) url.searchParams.set("uploader_id", this._filters.uploader_id)
      if (this._activeDivision) url.searchParams.set("division", this._activeDivision)
      if (this._specSetFilter) url.searchParams.set("spec_set_id", this._specSetFilter)
      // AI answer filter: only the documents the Ask-AI answer drew from.
      if (this._aiSources) {
        [...new Set(this._aiSources.map(s => String(s.id)))].forEach(id => url.searchParams.append("doc_ids[]", id))
      }

      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      if (seq !== this._loadSeq) return // a newer reload superseded this request

      if (this.hasSkeletonTarget) this.skeletonTarget.classList.add("hidden")

      if (page === 1) {
        this.tableBodyTarget.innerHTML = ""
        if (this.hasGridContainerTarget) this.gridContainerTarget.innerHTML = ""
        this._fileIds = []
        this._filesData = {}
        this._rowIndex = 0
        this._contentMatches = {}
      }

      // Per-document "matches inside the text" (count + page snippets),
      // present only while a search term is active.
      Object.assign(this._contentMatches ||= {}, data.content_matches || {})
      // AI answer filter: badges + snippets come from the answer's own quotes.
      if (this._aiSources) this._mergeAiMatches()
      if (page === 1 && this._filters.search && !this._aiSources) {
        track("Specifications Searched", {
          project_id: this.projectIdValue,
          results_count: data.total_count,
          documents_with_text_matches: Object.keys(data.content_matches || {}).length
        })
      }

      data.files.forEach(file => {
        this._fileIds.push(String(file.id))
        this._filesData[String(file.id)] = file
        // Also register nested prior versions so they can be selected and
        // compared even when the "Historical" view isn't toggled on.
        ;(file.versions || []).forEach(v => { this._filesData[String(v.id)] = v })
        // Individual drawings come nested under their standard-drawing packet
        // (not as their own list rows) — register them so they're openable.
        ;(file.drawings || []).forEach(d => { this._filesData[String(d.id)] = d })

        // Grid view appends cards progressively (no tree); list view is
        // (re)built as a tree below so combined + version nesting + the
        // expand/collapse arrows render on first load, not just after a sort.
        if (!this._sort.key && this.currentViewValue !== "list") {
          this.hasGridContainerTarget && this.gridContainerTarget.insertAdjacentHTML("beforeend", this._renderCard(file))
        }
      })
      if (this.currentViewValue === "list") this._reRenderTable()

      // Update breadcrumb count
      const badge = document.getElementById("specs-breadcrumb-count")
      if (badge) badge.textContent = data.total_count

      // Keep the sidebar nav counter in sync (after delete/upload/etc.).
      this._updateNavCount(data.nav_count)

      // AI Radar deep link: open the cited document once it's loaded, with
      // the cited passage searched & highlighted in the viewer.
      if (this._aiOpenId && this._filesData[String(this._aiOpenId)]) {
        const openId = this._aiOpenId, openPage = this._aiOpenPage, openHl = this._aiOpenHl
        this._aiOpenId = null
        // Land ON the cited row first — scrolled to and marked — so the
        // answer's source is findable even after the viewer is closed
        // (Phelipe 2026-08-21).
        this._markCitedRow(openId)
        setTimeout(() => this._openInViewer(openId, { initialPage: openPage, searchTerm: openHl }), 250)
      }
      // ChatLog sources: the list filters to the cited document, the row
      // pulses, and the viewer opens on the cited page (Phelipe 2026-08-25).
      if (this._aiLocateId) {
        const locateId = this._aiLocateId
        const locatePage = this._aiOpenPage
        this._aiLocateId = null
        this._updateAiChip()
        this._markCitedRow(locateId)
        if (this._filesData[String(locateId)]) {
          setTimeout(() => this._openInViewer(locateId, { initialPage: locatePage }), 250)
        }
      }

      // Update division tabs + export + spec set filter
      if (page === 1) {
        this._renderDivisionTabs(data.division_counts)
        this._divisionCounts = data.division_counts
        this._specSetsCache = data.spec_sets || []
        this._populateSpecSetFilter(data.spec_sets || [])
      }

      // Handle empty state
      if (page === 1 && data.files.length === 0) {
        this.emptyTarget.classList.remove("hidden")
        this.tableContainerTarget.classList.add("hidden")
        this.hasGridContainerTarget && this.gridContainerTarget.classList.add("hidden")
        if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
      } else {
        this.emptyTarget.classList.add("hidden")
        if (this.currentViewValue === "list") {
          this.tableContainerTarget.classList.remove("hidden")
        } else {
          this.hasGridContainerTarget && this.gridContainerTarget.classList.remove("hidden")
        }
      }

      this._isLastPage = data.is_last_page
      this._page = page + 1

      if (!this._isLastPage && this.hasSentinelTarget) {
        this.sentinelTarget.classList.remove("hidden")
        this._observer.observe(this.sentinelTarget)
      } else if (this.hasSentinelTarget) {
        this.sentinelTarget.classList.add("hidden")
        this._observer.disconnect()
      }
    } catch (error) {
      console.error("[Specifications] Failed to load page:", error)
      if (this.hasSkeletonTarget) this.skeletonTarget.classList.add("hidden")
      // Don't strand the user on a silently blank table — say what happened.
      if (page === 1 && seq === this._loadSeq && this.tableBodyTarget.children.length === 0) {
        this.tableBodyTarget.innerHTML =
          '<tr><td colspan="9" class="px-4 py-10 text-center text-[13px] text-gray-500">' +
          "Couldn't load the list — check your connection and try again." +
          "</td></tr>"
        if (this.hasTableContainerTarget) this.tableContainerTarget.classList.remove("hidden")
      }
    } finally {
      this._loading = false
    }
  }

  _resetAndLoad() {
    this._page = 1
    this._isLastPage = false
    this._loading = false
    // Stale-response guard: a reload invalidates any in-flight page fetch
    // so a slow older response can't clobber a newer one.
    this._loadSeq = (this._loadSeq || 0) + 1
    this._fileIds = []
    this._filesData = {}
    this._rowIndex = 0
    if (this._observer) this._observer.disconnect()
    this.tableBodyTarget.innerHTML = ""
    if (this.hasGridContainerTarget) this.gridContainerTarget.innerHTML = ""
    // Show the loading skeleton instead of a silently blank table while
    // the (possibly slow) search/filter request is in flight.
    if (this.hasSkeletonTarget) this.skeletonTarget.classList.remove("hidden")
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
    this._loadPage(1)
  }

  // ── Private: Render Table Row ─────────────────────────────────────

  // Build the table body. For Builder, nest each combined set's extracted
  // children under it (expandable), grouped by division: one header row per
  // division with its sections inside and that division's standard-drawing
  // packets at the bottom. Owners render flat, exactly as before.
  _renderRowsTree(sorted) {
    if (!this.builderValue) return sorted.map(f => this._renderRow(f)).join("")
    // Only nest a section under its combined parent if that parent is actually
    // present; if the combined was deleted, its sections render as top-level.
    const presentIds = new Set(sorted.map(f => String(f.id)))
    const childrenByParent = {}
    sorted.forEach(f => { if (f.combined_parent_id && presentIds.has(String(f.combined_parent_id))) (childrenByParent[f.combined_parent_id] ||= []).push(f) })
    // Individual drawings arrive nested on their packet row (file.drawings),
    // not as top-level rows — index them by packet id so the packet can render
    // them beneath it (SD-301, SD-302 …).
    sorted.forEach(f => { if (f.standard_drawing && Array.isArray(f.drawings) && f.drawings.length) childrenByParent[f.id] = f.drawings })
    this._childrenByParent = childrenByParent
    // Render a section; if it has prior versions, give it an arrow and nest
    // those versions (collapsed) beneath it. `parentOpen` is the whole ancestor
    // chain being expanded — a section inside a collapsed division stays hidden.
    const renderSection = (f, opts = {}, parentOpen = true) => {
      const vers = f.versions || []
      if (!vers.length) return this._renderRow(f, { ...opts, hidden: !parentOpen })
      const key = `s${f.id}`
      const open = this._groupExpanded(key, false) // versions collapsed by default
      let out = this._renderRow(f, { ...opts, hidden: !parentOpen, expandable: true, collapsed: !open, groupKey: key })
      vers.forEach(v => { out += this._renderRow(v, { child: true, grandchild: opts.grandchild, parentId: f.id, version: true, hidden: !(parentOpen && open) }) })
      return out
    }
    // Division groups start collapsed for a clean division-list overview; when
    // a search / AI filter / division tab is active, keep them expanded so the
    // matching sections aren't hidden inside a closed group. Otherwise each
    // group's open/closed state comes from the user's saved preference.
    const searching = !!(this._filters.search || this._aiSources || this._activeDivision)
    let html = ""
    const loose = [] // top-level sections (no present combined parent) — grouped by division after the books
    sorted.forEach(f => {
      if (f.combined_parent_id && presentIds.has(String(f.combined_parent_id))) return // rendered under its present parent
      const kids = childrenByParent[f.id]
      if (f.combined && kids && kids.length) {
        const bookOpen = searching ? true : this._groupExpanded(`c${f.id}`, true) // book expanded by default
        html += this._renderRow(f, { expandable: true, collapsed: !bookOpen, groupKey: `c${f.id}` })
        html += this._renderDivisionGroups(kids, f.id, 1, renderSection, searching, bookOpen)
      } else if (f.combined) {
        html += this._renderRow(f, {})
      } else {
        loose.push(f)
      }
    })
    html += this._renderDivisionGroups(loose, null, 0, renderSection, searching, true)
    return html
  }

  // Bucket sibling rows into one group per division (CSI order), each holding
  // its sections with the division's standard-drawing packets at the bottom.
  // Packets map to a division through their ISPWC series (SD-400 ↔ sections
  // 401-499); packets no section claims fall into a trailing "Standard
  // Drawings" group, sections with no division into "Other".
  _renderDivisionGroups(rows, parentId, depth, renderSection, searching = false, parentOpen = true) {
    if (!rows.length) return ""
    const sections = rows.filter(r => !r.standard_drawing)
    const stdDwgs = rows.filter(r => r.standard_drawing)
      .sort((a, b) => (parseInt(String(a.number || "").replace(/\D/g, ""), 10) || 0) - (parseInt(String(b.number || "").replace(/\D/g, ""), 10) || 0))

    const seriesFor = numStr => {
      const m = String(numStr || "").trim().match(/^(\d{3,4})\b/)
      if (!m) return null
      const ser = Math.floor(parseInt(m[1], 10) / 100) * 100
      return ser >= 100 ? ser : null
    }

    // Group by the BOOK's own division number (ISPWC: sections 201-299 →
    // Div 200) so the groups match the printed PDF — one group per 100-block.
    // Sections without a numeric series (CSI books, "03 30 00") fall back to
    // grouping by their division name, exactly as before.
    const buckets = new Map() // key -> { series, names: tally, sections, sds }
    const bucketFor = key => {
      if (!buckets.has(key)) buckets.set(key, { series: null, names: {}, sections: [], sds: [] })
      return buckets.get(key)
    }
    sections.forEach(s => {
      const ser = seriesFor(s.number)
      const b = bucketFor(ser ? `ser-${ser}` : (s.division || "Other"))
      if (ser) b.series = ser
      if (s.division) b.names[s.division] = (b.names[s.division] || 0) + 1
      b.sections.push(s)
    })
    stdDwgs.forEach(sd => {
      const ser = seriesFor(String(sd.number || "").replace(/\D/g, "")) // "SD-400" → 400
      const key = ser && buckets.has(`ser-${ser}`) ? `ser-${ser}` : "Standard Drawings"
      bucketFor(key).sds.push(sd)
    })

    // Group label: prefer the book's own division name, which its standard-
    // drawings packet carries ("Water Standard Drawings" → "Water"); fall back
    // to the most common extracted division name in the group.
    const labelFor = (key, b) => {
      if (key === "Standard Drawings") return "Standard Drawings"
      if (!b.series) return key
      const packet = b.sds[0]
      if (packet && packet.title) {
        const t = String(packet.title).replace(/\s*standard drawings\s*$/i, "").trim()
        if (t) return t
      }
      let best = null, n = 0
      for (const [d, c] of Object.entries(b.names)) if (c > n) { best = d; n = c }
      return best || `Division ${b.series}`
    }

    // Sort by the book's own division number when present (100, 200, 300 …);
    // CSI-name code is the fallback. NB: looked up name → numeric code, NOT
    // Object.values index — JS enumerates integer-like keys ("10".."33")
    // before "01".."09", which would scramble the CSI sequence.
    const codeByName = {}
    for (const [code, name] of Object.entries(CSI_DIVISIONS)) codeByName[name] = parseInt(code, 10)
    const orderOf = key => {
      if (key === "Standard Drawings") return 99999 // unmatched packets last
      const b = buckets.get(key)
      if (b && b.series) return b.series // book order
      if (key === "Other") return 99998
      return 90000 + (codeByName[key] || 900)
    }
    const keys = [...buckets.keys()].sort((a, b) => orderOf(a) - orderOf(b) || String(a).localeCompare(String(b)))

    // The group's actual CSI division (most common among its sections) — shown
    // as a badge in the Division column so the parent row matches its children.
    const divisionNameFor = b => {
      let best = null, n = 0
      for (const [d, c] of Object.entries(b.names)) if (c > n) { best = d; n = c }
      return best
    }

    let html = ""
    keys.forEach((key, i) => {
      const b = buckets.get(key)
      const gid = `divgrp-${parentId || "top"}-${i}`
      // Stable per-user key (book id + series/name), independent of sort index.
      const groupKey = `d${parentId || "top"}:${b.series || key}`
      const open = searching ? true : this._groupExpanded(groupKey, false) // collapsed by default
      html += this._renderDivisionGroupRow({
        gid, parentId, name: labelFor(key, b), divisionName: divisionNameFor(b),
        sectionCount: b.sections.length, sdCount: b.sds.length, depth, series: b.series,
        collapsed: !open, hidden: !parentOpen, groupKey
      })
      const childOpen = parentOpen && open
      const childOpts = depth > 0
        ? { child: true, grandchild: true, parentId: gid }
        : { child: true, parentId: gid }
      b.sections.forEach(s => { html += renderSection(s, childOpts, childOpen) })
      b.sds.forEach(s => {
        // A packet that's been split into individual drawings (SD-301 …)
        // renders as an expandable row with those drawings nested beneath
        // it; an un-split packet renders as a normal leaf row.
        const drawings = (this._childrenByParent && this._childrenByParent[s.id]) || []
        if (drawings.length) {
          const pkey = `sd${s.id}`
          const open = searching ? true : this._groupExpanded(pkey, false) // drawings collapsed by default
          html += this._renderRow(s, { ...childOpts, expandable: true, collapsed: !open, groupKey: pkey, hidden: !childOpen })
          drawings
            .slice()
            .sort((a, b2) => (parseInt(String(a.number || "").replace(/\D/g, ""), 10) || 0) - (parseInt(String(b2.number || "").replace(/\D/g, ""), 10) || 0))
            .forEach(dw => { html += this._renderRow(dw, { child: true, grandchild: true, parentId: s.id, hidden: !(childOpen && open) }) })
        } else {
          html += renderSection(s, childOpts, childOpen)
        }
      })
    })
    return html
  }

  // Synthetic division header row (expanded by default) — groups a division's
  // sections; nested inside a combined book or at the top level.
  _renderDivisionGroupRow({ gid, parentId, name, divisionName, sectionCount, sdCount, depth, series = null, collapsed = false, hidden = false, groupKey = "" }) {
    const checkboxCol = this.canManageDocumentsValue
      ? `<td class="pl-3.5 pr-2 py-2 w-8 ${this._bulkMode ? "" : "hidden"}" data-checkbox-col></td>`
      : ""
    const treeAttr = parentId ? `data-row-child-of="${parentId}"` : ""
    const isSdGroup = name === "Standard Drawings"
    const badgeColor = isSdGroup ? "bg-sky-50 text-sky-700" : (DIVISION_COLORS[name] || "bg-gray-100 text-gray-600")
    // Badge carries the book's own division number (e.g. "200") so it matches
    // the printed PDF; no badge at all when the book has no series.
    const badgeLabel = isSdGroup ? "Drawings" : (series ? `${series}` : "")
    const badgeHtml = badgeLabel
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${badgeColor}">${badgeLabel}</span>`
      : ""
    // Division-column label so the parent row reads like its child sections.
    const divBadge = divisionName
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${DIVISION_COLORS[divisionName] || "bg-gray-100 text-gray-600"}">${this._escapeHtml(divisionName)}</span>`
      : ""
    const counts = [
      sectionCount ? `${sectionCount} section${sectionCount === 1 ? "" : "s"}` : null,
      sdCount ? `${sdCount} drawing packet${sdCount === 1 ? "" : "s"}` : null
    ].filter(Boolean).join(", ")
    return `<tr class="bg-gray-50/80 border-b border-gray-200 cursor-pointer transition-colors hover:bg-brand/[0.04] ${hidden ? "hidden" : ""}"
                ${treeAttr} data-file-id="${gid}"
                data-action="click->specifications#groupRowClick">
      <td class="${depth > 0 ? "pl-6" : "pl-2"} pr-2 py-2 align-middle">
        <button type="button" onclick="event.stopPropagation()" data-action="click->specifications#toggleCombinedRow" data-parent-id="${gid}" data-group-key="${groupKey}" data-expanded="${collapsed ? "false" : "true"}" class="align-middle p-1.5 -m-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 cursor-pointer"><svg data-chevron class="w-4 h-4 inline transition-transform${collapsed ? " -rotate-90" : ""}" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></button>
      </td>
      ${checkboxCol}
      <td class="px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap">${badgeHtml}</td>
      <td class="px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap"><span class="font-semibold text-gray-800">${this._escapeHtml(name)}</span> <span class="text-gray-400">· ${counts}</span></td>
      <td class="px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap">${divBadge}</td>
      <td class="py-2"></td><td class="py-2 spec-set-col"></td><td class="py-2"></td><td class="py-2"></td>
    </tr>`
  }

  // Clicking a group row expands/collapses its children — same as the arrow.
  // (Group rows are synthetic, so there's no document to open.)
  groupRowClick(event) {
    const btn = event.currentTarget.querySelector("[data-parent-id]")
    if (btn) btn.click()
  }

  // Expand/collapse a row's children (combined → divisions, division →
  // sections, or section → versions). Collapsing hides ALL levels beneath so
  // re-expanding never surfaces an orphaned grandchild row.
  toggleCombinedRow(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const pid = btn.dataset.parentId
    const collapse = btn.dataset.expanded !== "false"
    if (collapse) {
      this._collapseDescendants(pid)
    } else {
      this.tableBodyTarget.querySelectorAll(`[data-row-child-of="${pid}"]`).forEach(r => r.classList.remove("hidden"))
    }
    btn.dataset.expanded = collapse ? "false" : "true"
    const chev = btn.querySelector("[data-chevron]")
    if (chev) chev.classList.toggle("-rotate-90", collapse)
    // Remember this group's new state for the next visit (per user).
    this._recordGroup(btn.dataset.groupKey, !collapse)
  }

  // Recursively hide every descendant of pid, resetting nested chevrons to
  // collapsed and closing any open search-snippet rows along the way (those
  // are inserted siblings, not children, so they'd otherwise stay behind).
  _collapseDescendants(pid) {
    this.tableBodyTarget.querySelectorAll(`[data-row-child-of="${pid}"]`).forEach(r => {
      r.classList.add("hidden")
      const childBtn = r.querySelector("button[data-parent-id]")
      if (childBtn) {
        childBtn.dataset.expanded = "false"
        childBtn.querySelector("[data-chevron]")?.classList.add("-rotate-90")
      }
      const cid = r.dataset.fileId
      if (!cid) return
      const matchRow = this.tableBodyTarget.querySelector(`[data-match-row-for="${cid}"]`)
      if (matchRow) {
        matchRow.remove()
        r.querySelector("[data-match-chevron]")?.classList.remove("rotate-180")
      }
      this._collapseDescendants(cid)
    })
  }

  _renderRow(file, opts = {}) {
    const isSuperseded = file.status === "superseded"
    const rowBg = isSuperseded ? "bg-gray-50" : "bg-white"
    this._rowIndex++
    const cell = "px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap overflow-hidden text-ellipsis"

    const checkboxCol = this.canManageDocumentsValue
      ? `<td class="pl-3.5 pr-2 py-2 w-8 ${this._bulkMode ? "" : "hidden"}" data-checkbox-col data-controller="modal-toggle" data-action="click->modal-toggle#stop">
           <input type="checkbox" class="form-checkbox rounded" style="accent-color: var(--color-brand)"
                  data-file-checkbox data-file-id="${file.id}"
                  data-action="change->specifications#toggleFileSelect"
                  ${this._selectedIds.has(String(file.id)) ? "checked" : ""}>
         </td>`
      : ""

    const divBadge = file.division
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${DIVISION_COLORS[file.division] || "bg-gray-100 text-gray-600"}">${this._escapeHtml(file.division)}</span>`
      : '<span class="text-gray-400">—</span>'

    // Only offer "Compare to previous" when this section actually has an
    // older version to compare against.
    const compareItem = this._hasPreviousVersion(file)
      ? `<button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                     data-action="click->specifications#compareToPrevious" data-file-id="${file.id}">Compare to previous</button>`
      : ""

    const bluebeamItems = this._bluebeamMenuItems(file)

    // "Move to Drawings" (Phelipe 2026-09-14) — a file the bidding carry-over
    // put on the wrong page (hand uploads don't get it). Top-level rows only:
    // a split pack moves as a whole, so the sheets or sections cut from it
    // travel with their parent rather than one by one.
    const moveItem = (file.combined_parent_id || !file.from_bidding)
      ? ""
      : `<button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                 data-action="click->specifications#rowMove" data-file-id="${file.id}">Move to Drawings</button>`

    // "Extract drawings" — only on a standard-drawing PACKET (not on the
    // individual drawings it was split into). The job is idempotent, so we
    // offer it whether or not it's already been split.
    const extractItem = (file.standard_drawing && !this._isIndividualDrawing(file))
      ? `<button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                     data-action="click->specifications#extractDrawings" data-file-id="${file.id}">Extract drawings</button>`
      : ""

    // Owners/consultants get the full row menu; builder/GC users get a menu
    // with only the Bluebeam actions (edit/delete/compare/extract stay
    // owner-only).
    const menuItems = this.canManageDocumentsValue
      ? `${compareItem}
             ${extractItem}
             ${bluebeamItems}
             <button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                     data-action="click->specifications#rowEdit" data-file-id="${file.id}">Edit</button>
             ${moveItem}
             <button type="button" class="block w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                     data-action="click->specifications#rowDelete" data-file-id="${file.id}">Delete</button>`
      : (this.bluebeamAllowedValue ? bluebeamItems : "")

    const actionsCol = menuItems.trim()
      ? `<td class="py-2 text-center relative" data-controller="modal-toggle" data-action="click->modal-toggle#stop">
           <button type="button" class="p-1 rounded hover:bg-gray-100 cursor-pointer"
                   data-action="click->specifications#toggleRowMenu"
                   data-file-id="${file.id}">
             <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>
           </button>
           <div class="hidden fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-44 whitespace-normal"
                data-specifications-target="rowMenu">
             ${menuItems}
           </div>
         </td>`
      : `<td class="py-2"></td>`

    const textColor = isSuperseded ? "text-gray-400" : "text-gray-600"
    const sectionColor = isSuperseded ? "text-gray-400" : "text-gray-800"

    const treeAttr = opts.child ? `data-row-child-of="${opts.parentId}"` : ""
    const childBg = opts.child ? "bg-gray-50/50" : ""
    const chevCollapsed = opts.collapsed ? " -rotate-90" : ""
    const expandedAttr = opts.collapsed ? ' data-expanded="false"' : ""
    // Search-inside-documents: result count on the left of matching rows; the
    // chevron flips when the row is expanded to show its snippets.
    const match = this._contentMatches?.[String(file.id)]
    // The Section # column. Child rows (sections / versions) get a wider left
    // gutter so a version-expand arrow has room. But when a search result-count
    // chip sits on the row, shrink that gutter so the chip slides into it and
    // the full sheet number still fits beside it.
    const numCell = opts.child
      ? `${match ? "pl-1.5" : "pl-8"} pr-2 xl:pr-3 py-2 xl:py-2.5 whitespace-nowrap overflow-hidden text-ellipsis`
      : cell
    // Result-count chip on the LEFT of the section number.
    const matchBadge = match
      ? `<span class="inline-flex items-center gap-1 rounded-full bg-brand/10 text-brand px-2 py-px text-[13px] font-semibold align-middle mr-1.5 shrink-0" title="${match.count} result${match.count === 1 ? "" : "s"} inside this document — click to preview">${match.count}<svg data-match-chevron class="w-3 h-3 transition-transform" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></span>`
      : ""

    // "Extraction in Progress…" — shown on a standard-drawing PACKET while its
    // drawings are still being carved in the background. Driven by the
    // post-upload watch (_sdPendingIds), which is cleared when the watch ends
    // so a genuinely-empty packet (e.g. a cover sheet) doesn't spin forever.
    const sdExtracting = file.standard_drawing && !this._isIndividualDrawing(file) &&
      this._sdPendingIds?.has(String(file.id))
    const extractingBadge = sdExtracting
      ? `<span class="inline-flex items-center gap-1.5 ml-2 align-middle text-[13px] text-brand"><svg class="w-3.5 h-3.5 animate-spin shrink-0" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>Extraction in Progress…</span>`
      : ""
    // Hover thumbnail (Larry 2026-08-20): a small photo glyph beside the
    // section number — resting the mouse on it floats a page-1 preview.
    const thumbIcon = file.pdf_url && !file.combined
      ? `<span class="inline-flex align-middle mr-1.5 p-0.5 rounded text-gray-400 hover:text-brand hover:bg-brand/10 cursor-zoom-in" data-pdf-url="${this._escapeHtml(file.pdf_url)}" data-thumb-label="${this._escapeHtml([file.number, file.title].filter(Boolean).join(" — "))}" data-action="mouseenter->specifications#sheetPreviewShow mouseleave->specifications#sheetPreviewHide click->specifications#sheetPreviewHide"><svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Z"/></svg></span>`
      : ""
    return `<tr class="${rowBg} ${childBg} border-b border-gray-200 cursor-pointer transition-colors hover:bg-brand/[0.04] ${opts.hidden ? "hidden" : ""}"
                data-action="click->specifications#openFile"
                data-file-id="${file.id}" ${treeAttr}>
      <td class="${opts.grandchild ? "pl-10" : (opts.child ? "pl-6" : "pl-2")} pr-2 py-2 align-middle">${opts.expandable
        ? `<button type="button" onclick="event.stopPropagation()" data-action="click->specifications#toggleCombinedRow" data-parent-id="${file.id}"${opts.groupKey ? ` data-group-key="${opts.groupKey}"` : ""}${expandedAttr} class="align-middle p-1.5 -m-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 cursor-pointer" title="Show / hide"><svg data-chevron class="w-4 h-4 inline transition-transform${chevCollapsed}" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></button>`
        : ""}</td>
      ${checkboxCol}
      <td class="${numCell}">${matchBadge}${thumbIcon}${file.combined
        ? '<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium bg-blue-100 text-blue-700">Combined</span>'
        : opts.version
          ? `<span class="${sectionColor}">Version ${file.revision_number ? this._escapeHtml(String(file.revision_number)) : "—"}</span>`
          : `<span class="font-mono font-semibold ${sectionColor}">${file.number ? this._escapeHtml(file.number) : '<span class="text-gray-400 font-normal">—</span>'}</span>`}</td>
      <td class="${cell}">
        <span class="${file.title ? textColor : "text-gray-400"} truncate ${this._bluebeamChip(file) || extractingBadge ? "inline-block align-middle max-w-[60%]" : "block"}">${this._escapeHtml(file.title || file.file_name || "—")}</span>${this._bluebeamChip(file)}${extractingBadge}
      </td>
      <td class="${cell}">${isSuperseded ? '<span class="text-gray-400">' + this._escapeHtml(file.division || "—") + '</span>' : divBadge}</td>
      <td class="${cell} text-center">
        <span class="${textColor}">${file.revision_number ? `v${this._escapeHtml(file.revision_number)}` : "—"}</span>
      </td>
      <td class="${cell} ${textColor} spec-set-col"><span class="truncate block">${this._escapeHtml(file.spec_set_name || "—")}</span></td>
      <td class="${cell} ${textColor}">${this._escapeHtml(file.formatted_date || "")}</td>
      ${actionsCol}
    </tr>`
  }

  // ── Private: Render Grid Card ─────────────────────────────────────

  _renderCard(file) {
    const isSuperseded = file.status === "superseded"
    const mutedClass = isSuperseded ? "opacity-50" : ""
    const icon = this._fileTypeIcon(file.extension, "w-10 h-10")
    const checkboxHtml = this.canManageDocumentsValue
      ? `<div class="absolute top-2 left-2 z-10" data-controller="modal-toggle" data-action="click->modal-toggle#stop">
           <input type="checkbox" class="form-checkbox rounded" style="accent-color: var(--color-brand)"
                  data-file-checkbox data-file-id="${file.id}"
                  data-action="change->specifications#toggleFileSelect"
                  ${this._selectedIds.has(String(file.id)) ? "checked" : ""}>
         </div>`
      : ""

    const preview = file.is_image && file.thumb_url
      ? `<img src="${file.thumb_url}" alt="" class="w-full h-full object-cover" loading="lazy">`
      : `<div class="flex items-center justify-center h-full bg-gray-50">${icon}</div>`

    const sectionOverlay = file.number
      ? `<div class="absolute bottom-1 left-1 bg-black/60 text-white text-[11px] font-mono font-bold px-1.5 py-0.5 rounded">${this._escapeHtml(file.number)}</div>`
      : ""

    const revBadge = file.revision_number
      ? `<span class="text-[11px] font-medium text-gray-400 ml-1">Rev ${this._escapeHtml(file.revision_number)}</span>`
      : ""

    return `<div class="relative rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow group ${mutedClass}"
                 data-action="click->specifications#openFile"
                 data-file-id="${file.id}">
      ${checkboxHtml}
      <div class="aspect-[4/3] overflow-hidden bg-gray-100 relative">
        ${preview}
        ${sectionOverlay}
      </div>
      <div class="px-2.5 py-2">
        <p class="text-[13px] font-medium text-gray-700 truncate">${this._escapeHtml(file.title || file.file_name || "")}</p>
        <p class="text-[13px] text-gray-400 mt-0.5">${this._escapeHtml(file.formatted_date || "")}${revBadge}</p>
      </div>
    </div>`
  }

  // ── Private: Division Tabs ──────────────────────────────────────

  _renderDivisionTabs(counts) {
    if (!this.hasDivisionMenuTarget || !counts) return

    this._divisionCounts = counts

    this.divisionMenuTarget.innerHTML = counts.map(div => {
      const isActive = (this._activeDivision === null && div.id === null) ||
                       String(div.id) === String(this._activeDivision)

      return `<button type="button"
                      class="w-full flex items-center justify-between px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-50 ${isActive ? 'text-brand font-semibold bg-brand/5' : 'text-gray-700'}"
                      data-action="click->specifications#selectDivision"
                      data-division="${div.id || ""}">
        <span>${this._escapeHtml(div.name)}</span>
        <span class="inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 rounded-full text-xs leading-none ${isActive ? 'bg-brand/15 text-brand font-bold' : 'bg-gray-100 text-gray-500'}">${div.count}</span>
      </button>`
    }).join("")

    this._updateDivisionLabel()
  }

  _updateDivisionLabel() {
    if (!this.hasDivisionLabelTarget || !this._divisionCounts) return
    const active = this._divisionCounts.find(d =>
      (this._activeDivision === null && d.id === null) ||
      String(d.id) === String(this._activeDivision)
    )
    if (active) {
      this.divisionLabelTarget.innerHTML = `${this._escapeHtml(active.name)} <span class="inline-flex items-center justify-center min-w-[18px] px-1.5 py-0.5 rounded-full text-xs leading-none bg-brand/15 text-brand font-bold">${active.count}</span>`
    }
  }

  _updateDivisionTabStyles() {
    if (!this.hasDivisionMenuTarget) return
    this.divisionMenuTarget.querySelectorAll("button").forEach(btn => {
      const div = btn.dataset.division
      const isActive = (this._activeDivision === null && (div === "null" || div === "")) ||
                       String(div) === String(this._activeDivision)
      if (isActive) {
        btn.classList.add("text-brand", "font-semibold", "bg-brand/5")
        btn.classList.remove("text-gray-700")
        btn.querySelector("span:last-child")?.classList.add("bg-brand/15", "text-brand", "font-bold")
        btn.querySelector("span:last-child")?.classList.remove("bg-gray-100", "text-gray-500")
      } else {
        btn.classList.remove("text-brand", "font-semibold", "bg-brand/5")
        btn.classList.add("text-gray-700")
        btn.querySelector("span:last-child")?.classList.remove("bg-brand/15", "text-brand", "font-bold")
        btn.querySelector("span:last-child")?.classList.add("bg-gray-100", "text-gray-500")
      }
    })
  }

  // ── Private: Spec Set Filter ───────────────────────────────────

  // Sets whose files were all deleted stay in the database. Keep them out of
  // the filter chips and the export dropdown (the active filter stays visible).
  _setsWithFiles(sets, activeId = null) {
    return (sets || []).filter(s => s.count !== 0 || (activeId && String(s.id) === String(activeId)))
  }

  _populateSpecSetFilter(sets) {
    if (!this.hasSpecSetFilterTarget) return
    sets = this._setsWithFiles(sets, this._specSetFilter)
    const activeCls = "bg-brand/10 text-brand font-medium border-brand/30"
    const inactiveCls = "text-gray-500 hover:bg-gray-50 border-gray-200"
    const isAllActive = !this._specSetFilter
    let html = `<button type="button"
                        class="px-2.5 py-1 text-[13px] rounded-full transition-colors cursor-pointer border ${isAllActive ? activeCls : inactiveCls}"
                        data-action="click->specifications#selectSpecSet"
                        data-set-id="">All</button>`
    sets.forEach(s => {
      const isActive = this._specSetFilter === String(s.id)
      html += `<button type="button"
                       class="px-2.5 py-1 text-[13px] rounded-full transition-colors cursor-pointer border ${isActive ? activeCls : inactiveCls}"
                       data-action="click->specifications#selectSpecSet"
                       data-set-id="${s.id}">${this._escapeHtml(s.name)}</button>`
    })
    this.specSetFilterTarget.innerHTML = html
  }

  async _fetchSpecSets() {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.specSetsUrlValue, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const sets = await res.json()
      this._specSetsCache = sets

      if (this.hasUploadSetSelectTarget) {
        let html = '<option value="">Select a spec set...</option>'
        sets.forEach(s => {
          html += `<option value="${s.id}">${this._escapeHtml(s.name)}${s.issued_date ? ` (${s.issued_date})` : ""}</option>`
        })
        this.uploadSetSelectTarget.innerHTML = html
      }
    } catch (e) {
      console.error("[Specifications] Failed to fetch spec sets:", e)
    }
  }

  // ── Private: Detail Slide-Over ────────────────────────────────────

  _openDetail(id) {
    id = String(id)
    this._activeFileId = id
    this._modalOpen = true
    const file = this._filesData[id]

    // Update navigation buttons — an individual standard drawing cycles
    // through its packet's sibling drawings, everything else pages the list.
    const nav = this._navIdsFor(id)
    const idx = nav.indexOf(id)
    this.prevBtnTarget.disabled = idx <= 0
    this.nextBtnTarget.disabled = idx < 0 || idx >= nav.length - 1

    // Show modal with animation
    this.detailModalTarget.classList.remove("hidden")
    this.detailLoadingTarget.classList.remove("hidden")
    this.detailPreviewTarget.innerHTML = ""
    this.detailMetaTarget.classList.add("hidden")
    if (this.hasDetailRevisionHistoryTarget) this.detailRevisionHistoryTarget.classList.add("hidden")
    if (this.hasDetailLinkedItemsTarget) this.detailLinkedItemsTarget.classList.add("hidden")

    requestAnimationFrame(() => {
      this.detailBackdropTarget.classList.add("opacity-100")
      this.detailBackdropTarget.classList.remove("opacity-0")
      this.detailPanelTarget.classList.remove("translate-x-full")
    })

    document.addEventListener("keydown", this._onKeydown)

    if (!file) {
      this.detailLoadingTarget.classList.add("hidden")
      this.detailPreviewTarget.innerHTML = '<p class="p-6 text-gray-400">File not found</p>'
      return
    }

    // Set title
    const titleText = file.number
      ? `${file.number}${file.title ? " — " + file.title : ""}`
      : file.file_name || "File"
    this.detailTitleTarget.querySelector("span").textContent = titleText
    this.detailDownloadBtnTarget.href = file.file_url || "#"

    this.detailLoadingTarget.classList.add("hidden")

    // Preview
    if (file.is_image && file.file_url) {
      this.detailPreviewTarget.innerHTML = `
        <div class="flex-1 flex items-center justify-center p-4 bg-gray-50">
          <img src="${file.file_url}" alt="${this._escapeHtml(file.file_name || "")}"
               class="max-w-full max-h-[70vh] object-contain rounded shadow-sm">
        </div>`
    } else if (file.is_pdf && file.file_url) {
      this.detailPreviewTarget.innerHTML = `
        <div class="flex-1 flex items-center justify-center p-8">
          <button type="button" class="flex flex-col items-center gap-3 px-8 py-6 rounded-xl border-2 border-dashed border-gray-300 hover:border-brand/50 hover:bg-brand/5 cursor-pointer transition-colors"
                  data-action="click->specifications#openPdfViewer" data-file-id="${file.id}">
            ${this._fileTypeIcon("pdf", "w-12 h-12")}
            <span class="text-sm font-medium text-gray-600">Open PDF Viewer</span>
          </button>
        </div>`
    } else {
      this.detailPreviewTarget.innerHTML = `
        <div class="flex-1 flex items-center justify-center p-8">
          <div class="text-center">
            ${this._fileTypeIcon(file.extension, "w-16 h-16 mx-auto mb-3")}
            <p class="text-base font-medium text-gray-600 mb-1">${this._escapeHtml(file.file_name || "")}</p>
            <p class="text-sm text-gray-400">.${this._escapeHtml(file.extension || "file")} file</p>
            ${file.file_url ? `<a href="${file.file_url}" target="_blank" rel="noopener" class="inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors">Download</a>` : ""}
          </div>
        </div>`
    }

    // Metadata
    const divBadge = file.division
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${DIVISION_COLORS[file.division] || "bg-gray-100 text-gray-600"}">${this._escapeHtml(file.division)}</span>`
      : '<span class="text-gray-400">—</span>'

    this.detailMetaTarget.classList.remove("hidden")
    this.detailMetaTarget.innerHTML = `
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Division</span>
        ${divBadge}
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Current Rev</span>
        <span>${this._escapeHtml(file.revision_number || "—")}${file.spec_set_name ? ` · ${this._escapeHtml(file.spec_set_name)}` : ""}${file.formatted_date ? ` · ${this._escapeHtml(file.formatted_date)}` : ""}</span>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Uploaded by</span>
        <span>${this._escapeHtml(file.uploader_name || "—")}</span>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">File</span>
        <span class="truncate max-w-[300px]">${this._escapeHtml(file.file_name || "—")}</span>
      </div>`

    // Fetch revision history
    this._loadSectionHistory(id)
  }

  async _loadSectionHistory(id) {
    if (!this.sectionHistoryUrlTemplateValue) return
    const url = this.sectionHistoryUrlTemplateValue.replace("__ID__", id)
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const data = await res.json()

      // Render revision history
      if (data.revisions && data.revisions.length > 0 && this.hasDetailRevisionHistoryTarget) {
        this.detailRevisionHistoryTarget.classList.remove("hidden")
        this.detailRevisionHistoryTarget.innerHTML = `
          <h4 class="text-[13px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Revision History</h4>
          <div class="space-y-1.5">
            ${data.revisions.map(rev => `
              <div class="flex items-center gap-3 text-[13px] ${rev.is_current ? "" : "text-gray-400"}">
                <span class="w-3 h-3 rounded-full ${rev.is_current ? "bg-brand" : "border-2 border-gray-300"} shrink-0"></span>
                <span class="font-medium ${rev.is_current ? "text-gray-800" : ""}">Rev ${this._escapeHtml(rev.revision_number || "?")}</span>
                <span class="text-gray-400">${this._escapeHtml(rev.spec_set_name || "")}</span>
                <span class="flex-1"></span>
                <span>${this._escapeHtml(rev.formatted_date || "")}</span>
                <span class="text-gray-400 truncate max-w-[80px]">${this._escapeHtml(rev.uploader_name || "")}</span>
                ${rev.file_url ? `<a href="${rev.file_url}" target="_blank" rel="noopener" class="text-brand hover:text-brand-dark text-[12px] font-medium">view</a>` : ""}
              </div>
            `).join("")}
          </div>`
      }

      // Render linked items
      if (data.linked_items && data.linked_items.length > 0 && this.hasDetailLinkedItemsTarget) {
        this.detailLinkedItemsTarget.classList.remove("hidden")
        this.detailLinkedItemsTarget.innerHTML = `
          <h4 class="text-[13px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Linked Action Items</h4>
          <div class="space-y-1.5">
            ${data.linked_items.map(item => `
              <div class="flex items-center gap-2 text-[13px]">
                <span class="font-medium text-gray-700">${this._escapeHtml(item.kind || "")}</span>
                <span class="text-gray-600 truncate flex-1">${this._escapeHtml(item.name || "")}</span>
                <span class="inline-flex items-center rounded px-1.5 py-px text-[11px] font-medium ${item.status === "open" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}">${this._escapeHtml(item.status || "")}</span>
              </div>
            `).join("")}
          </div>`
      }
    } catch (e) {
      console.error("[Specifications] section_history failed:", e)
    }
  }

  // ── Private: Combined PDF Split ──────────────────────────────────────

  async _checkForCombinedPdfs() {
    // Find large PDFs that haven't been split-checked yet
    const candidates = this._uploadFiles.filter(uf => {
      if (uf._splitChecked) return false
      const ext = uf.file.name.split(".").pop()?.toLowerCase()
      return ext === "pdf" && uf.file.size > 500 * 1024
    })

    if (candidates.length === 0) {
      this._extractMetadataForNewFiles()
      return
    }

    candidates.forEach(uf => { uf._splitChecked = true })
    this._splitCancelled = false

    // Show AI banner with timer — reuse the same blue banner as AI extraction
    this.uploadAiStatusTarget.classList.remove("hidden")
    this.uploadAiStatusTarget.classList.add("flex")
    this._aiTimerStart = performance.now()
    clearInterval(this._aiTimerInterval)
    const updateBannerText = (msg) => {
      const secs = Math.round((performance.now() - this._aiTimerStart) / 1000)
      this.uploadAiStatusTextTarget.innerHTML = `<span class="font-bold ai-rainbow-text">ConsLog AI — ${msg}</span> <span data-ai-timer class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${secs}s</span>`
    }
    updateBannerText("Scanning pages...")
    this._aiTimerInterval = setInterval(() => {
      const timerEl = this.uploadAiStatusTextTarget.querySelector("[data-ai-timer]")
      if (timerEl) timerEl.textContent = `${Math.round((performance.now() - this._aiTimerStart) / 1000)}s`
    }, 1000)
    // Restore rainbow animation on the icon
    const icon = this.uploadAiStatusTarget.querySelector("svg")
    if (icon) { icon.classList.add("ai-rainbow-icon"); icon.style.color = "" }

    let anySplit = false

    for (const uf of candidates) {
      if (this._splitCancelled) break

      try {
        let arrayBuffer = await uf.file.arrayBuffer()

        // Protected PDFs can't be read by the client-side splitter (pdf-lib),
        // so swap in a server-decrypted copy (HexaPDF) before scanning/splitting.
        if (this.hasDecryptUrlValue && pdfLooksEncrypted(arrayBuffer)) {
          try {
            const dToken = document.querySelector('meta[name="csrf-token"]')?.content
            const fd = new FormData()
            fd.append("file", uf.file)
            const dres = await fetch(this.decryptUrlValue, {
              method: "POST",
              headers: { "X-CSRF-Token": dToken || "" },
              credentials: "same-origin",
              signal: AbortSignal.timeout(120000),
              body: fd
            })
            if (dres.ok && dres.status !== 204) arrayBuffer = await dres.arrayBuffer()
          } catch (derr) {
            console.warn("[Specifications] decrypt failed, using original:", derr)
          }
        }

        // Phase 1: Extract page headers (client-side, fast)
        const result = await extractPageHeaders(arrayBuffer, (progress) => {
          if (this._splitCancelled) return
          if (progress.phase === "scanning" && progress.current) {
            updateBannerText(`Scanning pages... ${progress.current} of ${progress.total}`)
          }
        })

        if (!result || this._splitCancelled) continue

        // Phase 2: Send page headers to LLM for analysis
        updateBannerText("Analyzing spec book structure...")

        const token = document.querySelector('meta[name="csrf-token"]')?.content
        // Bound the AI request with a timeout + status check so a big book can
        // never spin forever; on any failure we fall back (book uploads as a
        // single file) instead of hanging.
        const res = await fetch(this.analyzeCombinedUrlValue, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin",
          signal: AbortSignal.timeout(120000),
          body: JSON.stringify({ pages: result.pages })
        })
        if (!res.ok) throw new Error(`analyze_combined HTTP ${res.status}`)
        const data = await res.json()
        const sections = data.sections || []
        // S2-15: an addendum's drawing sheets, filed on the Drawings page.
        const sheets = data.sheets || []

        // If the document is actually a drawing set, show warning and stop
        if (data.document_type === "drawings") {
          console.log(`[Specifications] ${uf.file.name} classified as drawings — showing warning`)
          clearInterval(this._aiTimerInterval)
          this.uploadAiStatusTarget.classList.add("hidden")
          this.uploadAiStatusTarget.classList.remove("flex")
          this.closeUploadModal()
          this._showWrongTypeModal()
          return
        }

        console.log(`[Specifications] LLM found ${sections.length} sections:`, sections.map(s => `${s.section_number} — ${s.title} (p${s.start_page})`))

        // Split a spec book — or an addendum with even one section and its
        // drawing sheets (S2-15).
        const splitIt = sections.length >= 3 || (sections.length >= 1 && sheets.length > 0)
        if (!splitIt || this._splitCancelled) continue

        // Phase 3: Split PDF progressively using LLM-identified sections
        const totalSections = sections.length
        updateBannerText(`Splitting into ${totalSections} sections...`)

        const splitter = splitSpecBySections(uf.file, arrayBuffer, sections, result.numPages, (progress) => {
          if (progress.phase === "splitting" && progress.current) {
            updateBannerText(`Splitting... ${progress.current} of ${progress.total} sections`)
          }
        })

        let firstSection = true
        let splitCount = 0
        const splitSectionNumbers = []
        const splitEntries = []

        for await (const section of splitter) {
          if (this._splitCancelled) break
          if (section === null) break

          if (firstSection) {
            firstSection = false
            anySplit = true
            this._uploadFiles = this._uploadFiles.filter(f => f.id !== uf.id)
            const origRow = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"]`)
            if (origRow) origRow.remove()
          }

          const id = Date.now() + Math.round(Math.random() * 1000)
          const entry = {
            id,
            file: section.file,
            sectionNumber: section.sectionNumber,
            title: section.title,
            division: section.division,
            standardDrawing: section.standardDrawing === true,
            _extracted: true,
            _splitChecked: true
          }
          this._uploadFiles.push(entry)
          splitEntries.push(entry)
          this._renderUploadSheetRow(id, section.file, {
            sectionNumber: section.sectionNumber,
            title: section.title,
            division: section.division
          })
          splitSectionNumbers.push(section.sectionNumber)
          splitCount++

          if (this.hasUploadSheetTableTarget) {
            this.uploadSheetTableTarget.classList.remove("hidden")
          }
        }

        // Batch check NEW/REV
        if (splitSectionNumbers.length > 0) {
          this._batchCheckSections(splitSectionNumbers)
        }

        // S2-15: the addendum's drawing sheets, cut out one page each and
        // filed on the Drawings page on upload.
        let sheetCount = 0
        if (splitCount > 0 && sheets.length > 0 && !this._splitCancelled) {
          updateBannerText(`Filing ${sheets.length} drawing sheet${sheets.length === 1 ? "" : "s"}...`)
          const sheetEntries = []
          for await (const sh of splitDrawingBySheets(uf.file, arrayBuffer, sheets, result.numPages)) {
            if (this._splitCancelled || sh === null) break
            const id = Date.now() + Math.round(Math.random() * 1000)
            const entry = {
              id,
              file: sh.file,
              sectionNumber: "",
              sheetNumber: sh.sheetNumber,
              title: sh.title,
              discipline: sh.discipline,
              _toDrawings: true,
              _extracted: true,
              _splitChecked: true
            }
            this._uploadFiles.push(entry)
            splitEntries.push(entry)
            sheetEntries.push(entry)
            this._renderDrawingSheetRow(entry)
            sheetCount++
          }
          this._checkDrawingSheets(sheetEntries)
        }

        // Final banner state + track split state for skip/undo
        if (splitCount > 0) {
          this._splitStates.push({
            originalFile: uf.file,
            splitEntries,
            splitCount,
            sheetCount
          })
          clearInterval(this._aiTimerInterval)
          // ConsLog Builder: keep the combined original alongside the
          // extracted sections (instead of the owner's either/or).
          if (this.builderValue) this._keepCombinedOriginal(uf.file)
          this._showSplitBannerWithSkipLink()
          const ic = this.uploadAiStatusTarget.querySelector(".ai-rainbow-icon")
          if (ic) { ic.classList.remove("ai-rainbow-icon"); ic.style.color = "#60a5ee" }
        }
      } catch (e) {
        console.error(`[Specifications] PDF split failed for ${uf.file.name}:`, e)
      }
    }

    if (!anySplit) {
      clearInterval(this._aiTimerInterval)
      this.uploadAiStatusTarget.classList.add("hidden")
      this.uploadAiStatusTarget.classList.remove("flex")
    }

    this._suggestSetName()
    this._extractMetadataForNewFiles()
  }

  async _batchCheckSections(sectionNumbers) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.checkSectionsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ section_numbers: sectionNumbers })
      })
      const results = await res.json()

      results.forEach(result => {
        // Find the upload file with matching section number
        const uf = this._uploadFiles.find(f => f.sectionNumber === result.section_number)
        if (!uf) return
        const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"]`)
        if (!row) return
        const sectionInput = row.querySelector(`input[data-upload-id="${uf.id}"]`)
        if (!sectionInput) return

        const existingLabel = row.querySelector("[data-sheet-label]")
        if (existingLabel) existingLabel.remove()

        const fileNameSpan = row.querySelector("td:first-child span")

        if (result.exists) {
          sectionInput.title = `Revises ${result.section_number} (currently Rev ${result.current_revision || "?"})`
          sectionInput.classList.add("bg-amber-50", "border-amber-300")
          sectionInput.classList.remove("bg-blue-50", "border-blue-200")
          fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-amber-500 ml-1.5 whitespace-nowrap">REV</span>`)
        } else {
          sectionInput.title = "New section"
          fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-emerald-500 ml-1.5 whitespace-nowrap">NEW</span>`)
        }
        this._showCopyMarkupsBox(uf.id, result)
      })
    } catch (e) {
      console.error("[Specifications] Batch check sections failed:", e)
    }
  }

  // ── Private: Upload Row Render ────────────────────────────────────

  // ── Addendum drawing sheets (S2-15, Jason/Starr) ──────────────────

  // The upload's set name and date, for the matching drawing set.
  _uploadSetNameAndDate(isNewSet) {
    if (isNewSet) {
      return {
        name: this.hasUploadSetNameInputTarget ? this.uploadSetNameInputTarget.value.trim() : "",
        date: this.hasUploadSetDateInputTarget ? this.uploadSetDateInputTarget.value : ""
      }
    }
    const id = this.hasUploadSetSelectTarget ? this.uploadSetSelectTarget.value : ""
    const set = (this._specSetsCache || []).find(s => String(s.id) === String(id))
    return { name: set?.name || "", date: set?.issued_date || "" }
  }

  // Register the drawing sheets cut off an addendum on the Drawings page,
  // in a drawing set named like the spec set ("Addendum 1"), reused if the
  // Drawings page already has one of that name. Returns how many were filed.
  async _fileDrawingSheets(sheetResults, token, isNewSet) {
    const { name, date } = this._uploadSetNameAndDate(isNewSet)
    try {
      const res = await fetch(this.drawingsRegisterUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({
          files: sheetResults.map(r => ({ cache_name: r.cache_name, filename: r.filename, sheet_number: r.sheet_number, title: r.title, discipline: r.discipline })),
          drawing_set_name: name,
          drawing_set_issued_date: date,
          reuse_drawing_set: true
        })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const n = (data.files || []).length
      track("Addendum Drawing Sheets Filed", { project_id: this.projectIdValue, sheets: n })
      return n
    } catch (e) {
      console.error("[Specifications] filing drawing sheets failed:", e)
      this._showToast("The drawing sheets couldn't be filed — upload them on the Drawings page", "error")
      return 0
    }
  }

  // A drawing-sheet row in the upload list: its sheet number and title, and
  // a "Drawings" pill where a section shows its division.
  _renderDrawingSheetRow(entry) {
    const { id, file } = entry
    this.uploadSheetRowsTarget.insertAdjacentHTML("beforeend", `
      <tr data-upload-row="${id}" class="border-b border-gray-100">
        <td class="py-1.5 pr-3">
          <div class="flex items-center gap-1 min-w-0">
            <span class="text-[13px] text-gray-700 truncate" title="${this._escapeHtml(file.name)}">${this._escapeHtml(file.name)}</span>
          </div>
          <div class="relative h-1 rounded-full mt-1 hidden" data-progress-track>
            <div data-progress-bar class="h-full bg-brand rounded-full transition-all duration-200" style="width: 0%"></div>
          </div>
        </td>
        <td class="py-1.5 px-3">
          <input type="text" class="w-full text-[13px] rounded-lg border bg-blue-50 border-blue-200 focus:border-brand focus:ring-0 py-1 px-2 font-mono"
                 placeholder="A-101" value="${this._escapeHtml(entry.sheetNumber || "")}"
                 data-upload-id="${id}" data-action="input->specifications#uploadDrawingSheetChanged">
        </td>
        <td class="py-1.5 px-3">
          <input type="text" data-title-input class="w-full text-[13px] rounded-lg border bg-blue-50 border-blue-200 focus:border-brand focus:ring-0 py-1 px-2"
                 placeholder="Drawing title" value="${this._escapeHtml(entry.title || "")}"
                 data-upload-id="${id}" data-action="input->specifications#uploadTitleChanged">
        </td>
        <td class="py-1.5 px-3">
          <span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium bg-sky-100 text-sky-700" title="Filed on the Drawings page">Drawings</span>
        </td>
        <td class="py-1.5 pl-3">
          <button type="button" class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  data-upload-id="${id}" data-action="click->specifications#removeUploadFile" title="Don't file this sheet">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </td>
      </tr>`)
  }

  uploadDrawingSheetChanged(event) {
    const uf = this._uploadFiles.find(f => f.id === parseInt(event.currentTarget.dataset.uploadId))
    if (uf) uf.sheetNumber = event.currentTarget.value.trim()
  }

  // NEW / REV / OLDER beside each drawing sheet, from the Drawings page's
  // own check (OLDER: dated before that sheet's current version).
  async _checkDrawingSheets(entries) {
    if (!this.drawingsCheckSheetsUrlValue || !entries.length) return
    const isNewSet = !this.hasUploadSetModeSelectTarget || this.uploadSetModeSelectTarget.value === "new"
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.drawingsCheckSheetsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ sheet_numbers: entries.map(e => e.sheetNumber), issued_date: this._uploadSetNameAndDate(isNewSet).date })
      })
      if (!res.ok) return
      const results = await res.json()
      entries.forEach((e, i) => {
        const r = results[i]
        const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${e.id}"]`)
        const nameSpan = row?.querySelector("td:first-child span")
        if (!r || !nameSpan) return
        row.querySelector("[data-sheet-label]")?.remove()
        if (r.exists && r.existing_sheet_number) e.sheetNumber = r.existing_sheet_number
        const [text, color] = !r.exists ? ["NEW", "text-emerald-500"] : r.older ? ["OLDER", "text-gray-500"] : ["REV", "text-amber-500"]
        nameSpan.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[13px] font-bold ${color} ml-1.5 whitespace-nowrap">${text}</span>`)
      })
    } catch (e) {
      console.error("[Specifications] check_sheets failed:", e)
    }
  }

  _renderUploadSheetRow(id, file, prefilled) {
    const divisionOptions = Object.values(CSI_DIVISIONS).map(name =>
      `<option value="${name}">${name}</option>`
    )
    // Deduplicate since Object.values may have unique values already
    const uniqueOptions = [...new Set(Object.values(CSI_DIVISIONS))].map(name =>
      `<option value="${name}" ${prefilled?.division === name ? "selected" : ""}>${name}</option>`
    ).join("")

    const preClass = prefilled ? "bg-blue-50 border-blue-200" : "border-gray-300"

    this.uploadSheetRowsTarget.insertAdjacentHTML("beforeend", `
      <tr data-upload-row="${id}" class="border-b border-gray-100">
        <td class="py-1.5 pr-3">
          <div class="flex items-center gap-1 min-w-0">
            <span class="text-[13px] text-gray-700 truncate" title="${this._escapeHtml(file.name)}">${this._escapeHtml(file.name)}</span>
          </div>
          <div class="relative h-1 rounded-full mt-1 hidden" data-progress-track>
            <div data-progress-bar class="h-full bg-brand rounded-full transition-all duration-200" style="width: 0%"></div>
          </div>
        </td>
        <td class="py-1.5 px-3">
          <input type="text" class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2 font-mono"
                 placeholder="01 00 00"
                 value="${prefilled?.sectionNumber ? this._escapeHtml(prefilled.sectionNumber) : ""}"
                 data-upload-id="${id}"
                 data-action="input->specifications#uploadSectionNumberChanged blur->specifications#uploadSectionNumberBlur">
        </td>
        <td class="py-1.5 px-3">
          <input type="text" data-title-input class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2"
                 placeholder="Section title"
                 value="${prefilled?.title ? this._escapeHtml(prefilled.title) : ""}"
                 data-upload-id="${id}"
                 data-action="input->specifications#uploadTitleChanged">
        </td>
        <td class="py-1.5 px-3">
          <select data-division-input class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2"
                  data-upload-id="${id}"
                  data-action="change->specifications#uploadDivisionChanged">
            <option value="">—</option>
            ${uniqueOptions}
          </select>
        </td>
        <td class="py-1.5 pl-3">
          <button type="button" class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  data-upload-id="${id}" data-action="click->specifications#removeUploadFile">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </td>
      </tr>`)
  }

  _updateUploadBtnText() {
    const count = this._uploadFiles.length
    const hasCombined = this.builderValue && this._uploadFiles.some(uf => uf._isCombined)
    // The split-button dropdown only applies when a combined was kept.
    if (this.hasUploadMenuBtnTarget) this.uploadMenuBtnTarget.classList.toggle("hidden", !hasCombined)
    if (!hasCombined) this._closeUploadMenu()
    this.uploadSubmitBtnTarget.classList.remove("rounded-lg", "rounded-l-lg")
    this.uploadSubmitBtnTarget.classList.add(hasCombined ? "rounded-l-lg" : "rounded-lg")
    // S2-15: drawing sheets split off an addendum go to the Drawings page.
    const sheets = this._uploadFiles.filter(uf => uf._toDrawings).length
    const sheetsText = sheets ? ` + ${sheets} Drawing Sheet${sheets === 1 ? "" : "s"}` : ""
    if (hasCombined) {
      const extracted = this._uploadFiles.filter(uf => !uf._isCombined && !uf._toDrawings).length
      this.uploadSubmitBtnTarget.textContent = `Upload Combined & Extract ${extracted} Section${extracted === 1 ? "" : "s"}${sheetsText}`
      return
    }
    const specCount = count - sheets
    this.uploadSubmitBtnTarget.textContent = count > 0 ? `Upload ${specCount} spec${specCount === 1 ? "" : "s"}${sheetsText}` : "Upload"
  }

  // ── Skip / Undo splitting ──────────────────────────────────────

  // ConsLog Builder: keep the original combined PDF in the upload queue
  // alongside the split sections (marked `_isCombined` so it registers as
  // the combined set record). De-duped per original file.
  _keepCombinedOriginal(file) {
    if (this._uploadFiles.some(uf => uf._isCombined && uf.file === file)) return
    const origId = Date.now() + Math.round(Math.random() * 1000)
    const titleFromName = file.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim()
    this._uploadFiles.push({
      id: origId,
      file,
      sectionNumber: "Combined",
      title: titleFromName,
      division: "",
      _extracted: true,
      _splitChecked: true,
      _isCombined: true
    })
    this._renderCombinedFileRow(origId, file)
  }

  _showSplitBannerWithSkipLink() {
    const totalSections = this._splitStates.reduce((sum, s) => sum + s.splitCount, 0)
    const totalSheets = this._splitStates.reduce((sum, s) => sum + (s.sheetCount || 0), 0)
    const sheetsText = totalSheets ? ` · ${totalSheets} drawing sheet${totalSheets > 1 ? "s" : ""} to Drawings` : ""
    const currentTimer = this.uploadAiStatusTextTarget.querySelector("[data-ai-timer]")
    const timerHtml = currentTimer ? currentTimer.outerHTML : ""

    // Builder always keeps the combined + the sections, so there's no
    // either/or "Upload as Combined" skip button.
    if (this.builderValue) {
      this.uploadAiStatusTextTarget.innerHTML =
        `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — ${totalSections} section${totalSections > 1 ? "s" : ""} extracted${sheetsText} · combined kept</span> ${timerHtml}`
      return
    }

    this.uploadAiStatusTextTarget.innerHTML =
      `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — ${totalSections} section${totalSections > 1 ? "s" : ""} extracted${sheetsText}</span> ${timerHtml}` +
      `<button type="button" class="ml-2 px-2.5 py-0.5 text-[13px] font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 hover:text-gray-800 cursor-pointer transition-colors" data-action="click->specifications#skipSplitting">Upload as Combined</button>`
  }

  skipSplitting(event) {
    if (event) event.preventDefault()
    if (!this._splitStates || this._splitStates.length === 0) return

    for (const state of this._splitStates) {
      // Remove all split rows from DOM and _uploadFiles
      for (const entry of state.splitEntries) {
        this._removeUploadRow(entry.id)
        this._uploadFiles = this._uploadFiles.filter(f => f.id !== entry.id)
      }

      // Re-add original combined file as a single upload
      const origId = Date.now() + Math.round(Math.random() * 1000)
      state._restoredId = origId
      const titleFromName = state.originalFile.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim()
      this._uploadFiles.push({
        id: origId,
        file: state.originalFile,
        sectionNumber: "Combined",
        title: titleFromName,
        division: "",
        _extracted: true,
        _splitChecked: true
      })
      this._renderCombinedFileRow(origId, state.originalFile)
    }

    // Update banner with undo link
    const totalSections = this._splitStates.reduce((sum, s) => sum + s.splitCount, 0)
    const fileCount = this._splitStates.length
    const fileLabel = fileCount > 1 ? `${fileCount} files` : "single file"

    this.uploadAiStatusTextTarget.innerHTML =
      `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — Uploaded as ${fileLabel}</span>` +
      `<button type="button" class="ml-2 px-2.5 py-0.5 text-[13px] font-medium text-brand bg-white border border-brand/30 rounded-full hover:bg-brand/5 hover:border-brand/50 cursor-pointer transition-colors" data-action="click->specifications#undoSkipSplitting">Split into ${totalSections} section${totalSections > 1 ? "s" : ""}</button>`

    if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.remove("hidden")
    this._updateUploadBtnText()
    this.uploadSubmitBtnTarget.disabled = false
  }

  undoSkipSplitting(event) {
    if (event) event.preventDefault()
    if (!this._splitStates || this._splitStates.length === 0) return

    for (const state of this._splitStates) {
      // Remove the single-file row
      if (state._restoredId) {
        const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${state._restoredId}"]`)
        if (row) row.remove()
        this._uploadFiles = this._uploadFiles.filter(f => f.id !== state._restoredId)
      }

      // Re-add all split entries and re-render rows
      const sectionNumbers = []
      for (const entry of state.splitEntries) {
        this._uploadFiles.push(entry)
        if (entry._toDrawings) { this._renderDrawingSheetRow(entry); continue }
        this._renderUploadSheetRow(entry.id, entry.file, {
          sectionNumber: entry.sectionNumber,
          title: entry.title,
          division: entry.division
        })
        sectionNumbers.push(entry.sectionNumber)
      }

      // Re-run batch check for NEW/REV styling
      if (sectionNumbers.length > 0) {
        this._batchCheckSections(sectionNumbers)
      }
    }

    // Restore banner with skip link
    this._showSplitBannerWithSkipLink()
    this._updateUploadBtnText()
  }

  _renderCombinedFileRow(id, file) {
    const titleFromName = file.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim()
    this.uploadSheetRowsTarget.insertAdjacentHTML("beforeend", `
      <tr data-upload-row="${id}" class="border-b border-gray-100">
        <td class="py-1.5 pr-3">
          <div class="flex items-center gap-1 min-w-0">
            <span class="text-[13px] text-gray-700 truncate" title="${this._escapeHtml(file.name)}">${this._escapeHtml(file.name)}</span>
          </div>
          <div class="relative h-1 rounded-full mt-1 hidden" data-progress-track>
            <div data-progress-bar class="h-full bg-brand rounded-full transition-all duration-200" style="width: 0%"></div>
          </div>
        </td>
        <td class="py-1.5 px-3" colspan="2">
          <input type="text" data-title-input class="w-full text-[13px] rounded-lg border border-gray-300 focus:border-brand focus:ring-0 py-1 px-2"
                 placeholder="Section title"
                 value="${this._escapeHtml(titleFromName)}"
                 data-upload-id="${id}"
                 data-action="input->specifications#uploadTitleChanged">
        </td>
        <td class="py-1.5 px-3"></td>
        <td class="py-1.5 pl-3">
          <button type="button" class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  data-upload-id="${id}" data-action="click->specifications#removeUploadFile">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </td>
      </tr>`)
  }

  _suggestSetName() {
    if (!this.hasUploadSetNameInputTarget) return
    // Only suggest if the user hasn't typed anything
    if (this.uploadSetNameInputTarget.value.trim()) return
    if (this._uploadFiles.length === 0) return

    // Strip extensions and find longest common prefix
    const names = this._uploadFiles.map(uf => uf.file.name.replace(/\.[^.]+$/, ""))
    if (names.length === 1) {
      this.uploadSetNameInputTarget.value = names[0]
      return
    }

    let prefix = names[0]
    for (let i = 1; i < names.length; i++) {
      while (names[i].indexOf(prefix) !== 0 && prefix.length > 0) {
        prefix = prefix.substring(0, prefix.length - 1)
      }
    }
    // Clean up trailing separators
    prefix = prefix.replace(/[-_\s.]+$/, "")
    if (prefix.length >= 3) {
      this.uploadSetNameInputTarget.value = prefix
    }
  }

  async _extractMetadataForNewFiles() {
    // Find PDF files that haven't been extracted yet
    const pdfFiles = this._uploadFiles.filter(uf => {
      if (uf._extracted) return false
      const ext = uf.file.name.split(".").pop()?.toLowerCase()
      return ext === "pdf"
    })
    if (pdfFiles.length === 0) {
      this._updateUploadBtnText()
      this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
      return
    }

    // Mark as in-progress so we don't re-extract
    pdfFiles.forEach(uf => { uf._extracted = true })

    // Show AI status banner with elapsed timer
    this.uploadAiStatusTarget.classList.remove("hidden")
    this.uploadAiStatusTarget.classList.add("flex")
    this._aiTimerStart = performance.now()
    this.uploadAiStatusTextTarget.innerHTML = `<span class="font-bold ai-rainbow-text">ConsLog AI — Analyzing Specifications</span> <span data-ai-timer class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">0s</span>`
    this._aiTimerInterval = setInterval(() => {
      const secs = Math.round((performance.now() - this._aiTimerStart) / 1000)
      const timerEl = this.uploadAiStatusTextTarget.querySelector("[data-ai-timer]")
      if (timerEl) timerEl.textContent = `${secs}s`
    }, 1000)

    // Process each PDF end-to-end independently: extract text/images → call server → apply result
    // Each file runs as its own promise so fast files populate immediately
    const MAX_PDF_BYTES = 250 * 1024 * 1024
    const MAX_TEXT_PAGES = 20
    const MAX_IMAGE_PAGES = 5
    const extractUrl = this.extractMetadataUrlValue
    let completed = 0
    const total = pdfFiles.length

    const promises = pdfFiles.map(async (uf) => {
      const fileStart = performance.now()
      try {
        if (uf.file.size > MAX_PDF_BYTES) {
          console.log(`[Specifications] Skipping ${uf.file.name} — too large (${(uf.file.size / 1024 / 1024).toFixed(1)}MB)`)
          return
        }

        console.log(`[Specifications] Processing ${uf.file.name} (${(uf.file.size / 1024).toFixed(0)}KB)...`)

        // Client-side: extract text or render images
        const arrayBuffer = await uf.file.arrayBuffer()
        const t1 = performance.now()
        const { pageTexts } = await extractPdfText(arrayBuffer.slice(0), MAX_TEXT_PAGES)
        console.log(`[Specifications] Text extraction took ${Math.round(performance.now() - t1)}ms for ${uf.file.name} (${pageTexts.length} pages, ${pageTexts.reduce((s, t) => s + t.length, 0)} chars)`)

        const allText = pageTexts.map((t, i) => `--- PAGE ${i + 1} ---\n${t}`).join("\n\n")
        const hasText = pageTexts.some(t => t.trim().length > 50)

        let mode, content
        if (hasText) {
          mode = "text"
          content = allText
          console.log(`[Specifications] Using text mode for ${uf.file.name} (${(content.length / 1024).toFixed(1)}KB payload)`)
        } else {
          const t2 = performance.now()
          console.log(`[Specifications] No embedded text in ${uf.file.name}, falling back to JPEG rendering...`)
          const { images } = await renderPdfPagesToJpegs(arrayBuffer, MAX_IMAGE_PAGES)
          const totalImageKB = images.reduce((s, img) => s + img.length, 0) / 1024
          console.log(`[Specifications] JPEG rendering took ${Math.round(performance.now() - t2)}ms for ${uf.file.name} (${images.length} pages, ${totalImageKB.toFixed(0)}KB base64)`)
          mode = "images"
          content = images
        }

        // Server-side: call Gemini via Rails proxy
        const t0 = performance.now()
        const result = await callExtractMetadata(extractUrl, mode, content, uf.file.name)
        console.log(`[Specifications] extract_metadata (${mode}) returned in ${Math.round(performance.now() - t0)}ms for ${uf.file.name}:`, result)
        console.log(`[Specifications] Total pipeline: ${Math.round(performance.now() - fileStart)}ms for ${uf.file.name}`)

        // Store extracted values. The DOCUMENT is the source of truth — the AI
        // reads the section number off the first pages, which beats whatever a
        // filename claims (files get renamed, downloaded, re-exported…). The
        // filename number is only a fallback when the document yields nothing
        // (e.g. an unreadable scan).
        const nameNumber = sectionFromFilename(uf.file.name)
        if (result.section_number) uf.sectionNumber = result.section_number
        else if (nameNumber) uf.sectionNumber = nameNumber
        if (result.title) uf.title = result.title
        if (result.division) uf.division = result.division
        if (!result.section_number && nameNumber) uf.division = detectDivision(nameNumber) || uf.division
      } catch (e) {
        console.error(`[Specifications] AI extraction failed for ${uf.file.name} after ${Math.round(performance.now() - fileStart)}ms:`, e)
      } finally {
        // Render row with extracted data (or empty fields if extraction failed)
        const prefilled = (uf.sectionNumber || uf.title || uf.division)
          ? { sectionNumber: uf.sectionNumber, title: uf.title, division: uf.division }
          : null
        this._renderUploadSheetRow(uf.id, uf.file, prefilled)
        if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.remove("hidden")
        if (uf.sectionNumber) this._checkSectionAfterExtract(uf.id, uf.sectionNumber)

        completed++
        if (completed === total) {
          clearInterval(this._aiTimerInterval)
          const totalSecs = Math.round((performance.now() - this._aiTimerStart) / 1000)
          this.uploadAiStatusTextTarget.innerHTML = `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — Values Extracted</span> <span class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${totalSecs}s</span>`
          const icon = this.uploadAiStatusTarget.querySelector(".ai-rainbow-icon")
          if (icon) { icon.classList.remove("ai-rainbow-icon"); icon.style.color = "#60a5ee" }

          // Restore skip link if any files were split in this batch
          if (this._splitStates && this._splitStates.length > 0) {
            this._showSplitBannerWithSkipLink()
          }
        }
      }
    })

    await Promise.allSettled(promises)
    // Re-enable upload button after AI processing
    this._updateUploadBtnText()
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
  }

  async _renderPdfPagesAsImages(file, maxPages) {
    const arrayBuffer = await file.arrayBuffer()
    pdfjsLib.GlobalWorkerOptions.workerSrc = "/assets/pdf.worker.js"
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
    const numPages = Math.min(pdf.numPages, maxPages)
    const images = []

    for (let i = 1; i <= numPages; i++) {
      const page = await pdf.getPage(i)
      // Render at 1.5x scale for readability while keeping size reasonable
      const viewport = page.getViewport({ scale: 1.5 })
      const canvas = document.createElement("canvas")
      canvas.width = viewport.width
      canvas.height = viewport.height
      const ctx = canvas.getContext("2d")
      await page.render({ canvasContext: ctx, viewport }).promise
      // Convert to JPEG at 70% quality — keeps each page around 100-200KB
      const dataUrl = canvas.toDataURL("image/jpeg", 0.7)
      const base64 = dataUrl.split(",")[1]
      images.push(base64)
    }

    return images
  }

  async _checkSectionAfterExtract(uploadId, sectionNumber) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.checkSectionsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ section_numbers: [sectionNumber] })
      })
      const results = await res.json()
      const result = results[0]
      const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uploadId}"]`)
      if (!row) return
      const sectionInput = row.querySelector('input[data-upload-id="' + uploadId + '"]')
      if (!sectionInput) return

      // Remove any existing label
      const nameCell = row.querySelector("[data-sheet-label]")
      if (nameCell) nameCell.remove()

      const fileNameSpan = row.querySelector("td:first-child span")

      if (result && result.exists) {
        sectionInput.title = `Revises ${result.section_number} (currently Rev ${result.current_revision || "?"})`
        sectionInput.classList.add("bg-amber-50", "border-amber-300")
        sectionInput.classList.remove("bg-blue-50", "border-blue-200")
        fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-amber-500 ml-1.5 whitespace-nowrap">REV</span>`)
      } else {
        sectionInput.title = "New section"
        fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-emerald-500 ml-1.5 whitespace-nowrap">NEW</span>`)
      }
      this._showCopyMarkupsBox(uploadId, result)
    } catch (e) {
      // Silently fail — the hint already shows AI-suggested
    }
  }

  // K7-23 (Larry 2026-10-06): under a row that revises a section whose
  // current version has markups, an unticked box to start the new version
  // with them. No markups on that version (or a new section) → no box.
  _showCopyMarkupsBox(id, result) {
    const uf = this._uploadFiles.find(f => f.id === id)
    const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"]`)
    const box = this.uploadSheetRowsTarget.querySelector(`[data-copy-markups-row="${id}"]`)
    if (!uf || !row || !result?.exists || !result?.has_markups) {
      box?.remove()
      row?.classList.add("border-b")
      if (uf) uf.copyMarkups = false
      return
    }
    if (box) return
    row.classList.remove("border-b")
    row.insertAdjacentHTML("afterend", `
      <tr data-copy-markups-row="${id}" class="border-b border-gray-100">
        <td colspan="5" class="pb-2 pr-3">
          <label class="inline-flex items-center gap-2 text-[13px] text-gray-600 cursor-pointer select-none">
            <input type="checkbox" class="rounded border-gray-300 text-brand focus:ring-0 cursor-pointer"
                   data-upload-id="${id}" ${uf.copyMarkups ? "checked" : ""}
                   data-action="change->specifications#toggleCopyMarkups">
            ${this._escapeHtml(this.copyMarkupsLabelValue)}
          </label>
        </td>
      </tr>`)
  }

  toggleCopyMarkups(event) {
    const uf = this._uploadFiles.find(f => f.id === parseInt(event.currentTarget.dataset.uploadId))
    if (uf) uf.copyMarkups = event.currentTarget.checked
  }

  _removeUploadRow(id) {
    this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"]`)?.remove()
    this.uploadSheetRowsTarget.querySelector(`[data-copy-markups-row="${id}"]`)?.remove()
  }

  // ── Private: Helpers ──────────────────────────────────────────────

  _removeFileFromDOM(id) {
    id = String(id)
    const row = this.tableBodyTarget.querySelector(`tr[data-file-id="${id}"]`)
    if (row) row.remove()
    this._fileIds = this._fileIds.filter(fid => fid !== id)
    delete this._filesData[id]
    this._selectedIds.delete(id)
  }

  // Sync the sidebar "Specifications" nav counter (hidden at 0).
  _updateNavCount(n) {
    if (n == null) return
    const el = document.querySelector('[data-nav-count="specifications"]')
    if (!el) return
    el.textContent = n
    el.classList.toggle("hidden", Number(n) === 0)
  }

  _updateBulkBar() {
    if (!this.hasBulkCountTarget) return
    this.bulkCountTarget.textContent = `${this._selectedIds.size} selected`
    if (this.hasCompareBtnTarget) {
      const { ok, reason } = this._compareEligibility()
      const btn = this.compareBtnTarget
      btn.classList.toggle("opacity-40", !ok)
      btn.classList.toggle("cursor-not-allowed", !ok)
      btn.classList.toggle("cursor-pointer", ok)
      btn.classList.toggle("hover:bg-brand/5", ok)
      if (ok) btn.removeAttribute("data-tip")
      else btn.setAttribute("data-tip", reason)
    }
  }

  // Compare requires exactly two section PDFs (the multi-page Combined file
  // can't be compared). Returns { ok, reason } so the button can grey out +
  // show a tooltip explaining the criteria.
  _compareEligibility() {
    const files = [...this._selectedIds].map(id => (this._filesData || {})[id]).filter(Boolean)
    if (files.length !== 2) return { ok: false, reason: "Select exactly 2 sections to compare." }
    if (!files.every(f => f.is_pdf)) return { ok: false, reason: "Both selected sections must be PDFs." }
    if (files.some(f => f.combined)) return { ok: false, reason: "The Combined file can't be compared — pick 2 individual sections." }
    return { ok: true, reason: "" }
  }

  // Whether this section has an older version we know about — either nested
  // under it (default view) or elsewhere in the loaded rows (historical view).
  _hasPreviousVersion(file) {
    if (!file || file.combined) return false
    if ((file.versions || []).length > 0) return true
    const key = file.number
    if (!key) return false
    const rev = parseInt(file.revision_number, 10) || 0
    return Object.values(this._filesData || {}).some(o =>
      o && String(o.id) !== String(file.id) && !o.combined &&
      o.number === key && ((parseInt(o.revision_number, 10) || 0) < rev)
    )
  }

  // Open the Apryse side-by-side compare on the two selected PDFs.
  // The OLDER version goes on the left, the newer on the right, so the
  // overlay diff reads "before → after" left-to-right.
  compareSelected() {
    if (!this._compareEligibility().ok) return
    const data = this._filesData || {}
    const [a, b] = [...this._selectedIds]
    const rank = (id) => {
      const f = data[id] || {}
      const statusRank = f.status === "current" ? 1 : 0 // current is newer
      const rev = parseInt(f.revision_number, 10)
      const created = Date.parse(f.created_at) || 0
      return [statusRank, isNaN(rev) ? 0 : rev, created]
    }
    const [ra, rb] = [rank(a), rank(b)]
    const aOlder = ra[0] !== rb[0] ? ra[0] < rb[0]
      : ra[1] !== rb[1] ? ra[1] < rb[1]
      : ra[2] <= rb[2]
    const [left, right] = aOlder ? [a, b] : [b, a]
    window.dispatchEvent(new CustomEvent("pdf-compare:open", {
      detail: { leftVersionId: left, rightVersionId: right }
    }))
  }

  // One-click "compare this section to its latest previous version".
  compareToPrevious(event) {
    if (event) event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    if (!id) return
    this._closeAllRowMenus?.()
    window.dispatchEvent(new CustomEvent("pdf-compare:open", {
      detail: { leftVersionId: id, rightVersionId: "previous" }
    }))
  }

  _dispatchCompare(left, right) {
    window.dispatchEvent(new CustomEvent("pdf-compare:open", {
      detail: { leftVersionId: left, rightVersionId: right }
    }))
  }

  // ── In-viewer compare picker ──────────────────────────────────────
  // Opened by the "Compare" button inside the PDF viewer. Lets the user
  // either diff against the previous revision or pick any other section.
  async openComparePicker(event) {
    const id = String(event?.detail?.fileId || "")
    const file = this._filesData?.[id]
    if (!id) return
    this._compareSourceId = id
    this._compareVersions = []
    // "split" = side-by-side (existing); "overlay" = Bluebeam-style stacked
    // tint view (old red / new blue), same-section versions only.
    this._compareMode = event?.detail?.mode === "overlay" ? "overlay" : "split"

    // Remember the ERB's default "no previous version" text once, so the
    // transient "Loading…" state can be swapped back afterwards.
    if (this.hasComparePickerEmptyTarget && this._comparePickerEmptyDefault == null) {
      this._comparePickerEmptyDefault = this.comparePickerEmptyTarget.textContent.trim()
    }

    if (this.hasComparePickerTitleTarget) {
      const label = file
        ? ([file.number, file.title].filter(Boolean).join(" — ") || file.file_name)
        : "section"
      this.comparePickerTitleTarget.textContent =
        `${this._compareMode === "overlay" ? "Overlay" : "Compare"} ${label}`
    }

    // Show the modal immediately, then load this section's version history.
    this._setComparePickerSections({ loading: true })
    this.comparePickerModalTarget.classList.remove("hidden")

    let versions = []
    try {
      const url = this.sectionHistoryUrlTemplateValue.replace("__ID__", id)
      const resp = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await resp.json()
      versions = Array.isArray(data.revisions) ? data.revisions : []
    } catch (_) { versions = [] }

    // Modal may have been closed while loading.
    if (this._compareSourceId !== id) return
    this._compareVersions = versions
    this._renderComparePicker(versions)
  }

  closeComparePicker() {
    if (this.hasComparePickerModalTarget) this.comparePickerModalTarget.classList.add("hidden")
    this._compareSourceId = null
    this._compareVersions = []
  }

  compareWithPrevious() {
    const id = this._compareSourceId
    if (!id) return
    this._launchCompare(id, "previous")
  }

  compareWithPicked(event) {
    const otherId = String(event.currentTarget.dataset.fileId || "")
    const id = this._compareSourceId
    if (!id || !otherId) return
    const [left, right] = this._orderVersions(id, otherId)
    this._launchCompare(left, right)
  }

  // Close the picker and open the side-by-side compare ON TOP of the viewer
  // (K7-20): closing the viewer first stepped the browser back a page,
  // which made Turbo restore the page and wipe out the compare too.
  _launchCompare(left, right) {
    if (this._compareMode === "overlay") return this._launchOverlay(left, right)
    this.closeComparePicker()
    this._dispatchCompare(left, right)
  }

  // Overlay two versions of the SAME section (Phelipe 2026-08-01): resolve
  // old vs new, then hand both to the viewer's overlay builder — stays
  // inside the viewer instead of closing it.
  _launchOverlay(left, right) {
    const vs = this._compareVersions || []
    let oldId, newId
    if (String(right) === "previous") {
      newId = String(left)
      const idx = vs.findIndex(v => String(v.id) === newId)
      const prev = (idx >= 0 && vs[idx + 1]) || vs.find(v => String(v.id) !== newId)
      if (!prev) { this.closeComparePicker(); return }
      oldId = String(prev.id)
    } else {
      oldId = String(left)
      newId = String(right)
    }
    const revOf = (id) => {
      const v = vs.find(x => String(x.id) === String(id))
      return v && v.revision_number != null ? `Rev ${v.revision_number}` : ""
    }
    const file = this._filesData?.[String(this._compareSourceId)] || {}
    const base = [file.number, file.title].filter(Boolean).join(" — ") || file.file_name || "Section"
    const title = `${base} · Overlay: ${revOf(oldId) || "older"} (red) vs ${revOf(newId) || "newer"} (blue)`

    this.closeComparePicker()
    const viewer = this.element.querySelector("[data-controller='action-item-pdf-viewer']")
    const viewerCtrl = viewer && this.application.getControllerForElementAndIdentifier(viewer, "action-item-pdf-viewer")
    if (!viewerCtrl || typeof viewerCtrl.openOverlayCompare !== "function") return
    const urlFor = (vid) => this.pdfViewerBaseUrlValue.replace(/\/0\//, `/${vid}/`)
    viewerCtrl.openOverlayCompare({ oldUrl: urlFor(oldId), newUrl: urlFor(newId), title })
  }

  // Order two versions of the same section so the OLDER one lands on the left.
  // section_history is newest-first, so a higher array index = older.
  _orderVersions(aId, bId) {
    const vs = this._compareVersions || []
    const idx = (vid) => vs.findIndex(v => String(v.id) === String(vid))
    return idx(aId) > idx(bId) ? [aId, bId] : [bId, aId]
  }

  // Show only the controls that apply to this section's version count:
  //   1 version  → nothing to compare (message)
  //   2 versions → "Compare to previous" only
  //   3+ versions→ "Compare to previous" + a list of older versions to pick
  _renderComparePicker(versions) {
    const count = versions.length
    this._setComparePickerSections({
      previous: count >= 2,
      older: count > 2,
      empty: count < 2,
      message: this._comparePickerEmptyDefault
    })

    if (count > 2 && this.hasComparePickerListTarget) {
      const others = versions.filter(v => String(v.id) !== String(this._compareSourceId))
      this.comparePickerListTarget.innerHTML = others.map(v => {
        const rev = this._escapeHtml(v.revision_number != null ? `Rev ${v.revision_number}` : "—")
        const date = this._escapeHtml(v.formatted_date || "")
        const who = this._escapeHtml(v.uploader_name || "")
        const meta = [date, who].filter(Boolean).join(" · ")
        const current = v.is_current ? `<span class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[13px] font-medium bg-emerald-100 text-emerald-700">Current</span>` : ""
        return `<button type="button" data-file-id="${v.id}"
                  data-action="click->specifications#compareWithPicked"
                  class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left hover:bg-gray-50 cursor-pointer transition-colors">
            <span class="text-[13px] font-semibold text-gray-800 w-24 shrink-0 truncate">${rev}${current}</span>
            <span class="min-w-0 flex-1">
              ${meta ? `<span class="block text-[13px] text-gray-500 truncate">${meta}</span>` : ""}
            </span>
          </button>`
      }).join("")
    }
  }

  // Toggle the picker's sections. Pass { loading: true } for the brief
  // history fetch, or explicit { previous, older, empty } booleans.
  _setComparePickerSections({ loading = false, previous = false, older = false, empty = false, message = null } = {}) {
    if (this.hasComparePickerPreviousWrapTarget) this.comparePickerPreviousWrapTarget.classList.toggle("hidden", loading || !previous)
    if (this.hasComparePickerOlderSectionTarget) this.comparePickerOlderSectionTarget.classList.toggle("hidden", loading || !older)
    if (this.hasComparePickerEmptyTarget) {
      if (loading) this.comparePickerEmptyTarget.textContent = "Loading…"
      else if (message != null) this.comparePickerEmptyTarget.textContent = message
      this.comparePickerEmptyTarget.classList.toggle("hidden", !(loading || empty))
    }
  }

  _updateFilterBadge() {
    if (!this.hasFilterBadgeTarget) return
    let count = 0
    if (this._filters.date_from || this._filters.date_to) count++
    if (this._filters.uploader_id) count++
    if (this._specSetFilter) count++

    if (count > 0) {
      this.filterBadgeTarget.classList.remove("hidden")
      this.filterBadgeTarget.classList.add("inline-flex")
      this.filterBadgeTarget.textContent = count
      if (this.hasClearFiltersRowTarget) this.clearFiltersRowTarget.style.display = ""
    } else {
      this.filterBadgeTarget.classList.add("hidden")
      this.filterBadgeTarget.classList.remove("inline-flex")
      if (this.hasClearFiltersRowTarget) this.clearFiltersRowTarget.style.display = "none"
    }
  }

  _restoreSavedFilters() {
    const saved = this.savedFiltersValue
    // Per-user expand/collapse memory rides alongside the saved filters.
    this._groupState = (saved && saved._groups) || {}
    if (!saved || Object.keys(saved).length === 0) return

    this._filters = { ...saved }
    delete this._filters._groups

    if (this._filters.search && this.hasSearchInputTarget) {
      this.searchInputTarget.value = this._filters.search
      this._toggleSearchClear()
    }
    if (this._filters.date_from && this.hasDateFromTarget) {
      this.dateFromTarget.value = this._filters.date_from
    }
    if (this._filters.date_to && this.hasDateToTarget) {
      this.dateToTarget.value = this._filters.date_to
    }

    this._updateFilterBadge()
  }

  _persistFilters() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const payload = { ...this._filters }
    if (this._groupState && Object.keys(this._groupState).length) payload._groups = this._groupState
    fetch(this.saveFiltersUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      credentials: "same-origin",
      body: JSON.stringify({ filters: payload })
    })
  }

  // Per-user memory of which combined / division / version groups the user
  // left open — restored on the next visit so the list keeps their layout.
  _groupExpanded(key, def) {
    return (this._groupState && key in this._groupState) ? this._groupState[key] : def
  }

  _recordGroup(key, expanded) {
    if (!key) return
    ;(this._groupState ||= {})[key] = expanded
    clearTimeout(this._groupSaveTimer)
    this._groupSaveTimer = setTimeout(() => this._persistFilters(), 400)
  }

  _onKeydown(event) {
    if (!this._modalOpen) return
    if (event.key === "Escape") {
      this.closeDetail()
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault()
      this.prevFile()
    } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault()
      this.nextFile()
    }
  }

  _escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  // ── Distribution Modal ────────────────────────────────────────────

  async _openDistributeModal(uploadedIds) {
    this._distributeFileIds = uploadedIds
    this._distributeSelectedEmails = new Map() // email -> { name, email }
    this._distributeLockedEmails = new Set() // emails with documents_notif (pre-checked, can't uncheck)
    this._distributeMembers = []

    // Fetch members grouped by company
    try {
      const resp = await fetch(this.distributionMembersUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!resp.ok) return
      this._distributeMembers = await resp.json()
    } catch {
      return
    }

    if (this._distributeMembers.length === 0) return

    // Pre-select users with documents_notif enabled
    for (const company of this._distributeMembers) {
      for (const user of company.users) {
        if (user.documents_notif) {
          this._distributeSelectedEmails.set(user.email, { name: user.name, email: user.email })
          this._distributeLockedEmails.add(user.email)
        }
      }
    }

    this.distributeSearchInputTarget.value = ""
    this._renderDistributeList()
    this._updateDistributeUI()
    this.distributeModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  filterDistributeMembers() {
    this._renderDistributeList()
  }

  _renderDistributeList() {
    const query = (this.distributeSearchInputTarget.value || "").toLowerCase().trim()
    let html = ""

    for (const company of this._distributeMembers) {
      const users = company.users.filter(u => {
        if (!query) return true
        return u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query)
      })
      if (users.length === 0) continue

      html += `<div class="px-5 pt-3 pb-1">
        <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">${this._escapeHtml(company.name)}</span>
      </div>`

      for (const user of users) {
        const checked = this._distributeSelectedEmails.has(user.email)
        const locked = this._distributeLockedEmails.has(user.email)
        const disabledAttr = locked ? "disabled" : ""
        const cursorClass = locked ? "cursor-default" : "cursor-pointer"
        const labelOpacity = locked ? "opacity-70" : ""

        html += `<label class="flex items-center gap-2.5 px-5 py-1.5 hover:bg-gray-50 ${cursorClass} select-none ${labelOpacity}">
          <input type="checkbox" class="form-checkbox rounded shrink-0" style="accent-color: var(--color-brand); width: 14px; height: 14px"
                 data-email="${this._escapeHtml(user.email)}"
                 data-name="${this._escapeHtml(user.name)}"
                 data-action="change->specifications#toggleDistributeMember"
                 ${checked ? "checked" : ""} ${disabledAttr}>
          <div class="min-w-0 flex-1">
            <div class="text-sm text-gray-800 truncate">${this._escapeHtml(user.name)}</div>
            <div class="text-[12px] text-gray-400 truncate">${this._escapeHtml(user.email)}</div>
          </div>
          ${locked ? '<span class="text-[11px] text-gray-400 shrink-0">auto</span>' : ""}
        </label>`
      }
    }

    if (!html) {
      html = '<div class="px-5 py-4 text-sm text-gray-400 text-center">No members found</div>'
    }

    this.distributeDropdownListTarget.innerHTML = html
  }

  toggleDistributeMember(event) {
    const cb = event.currentTarget
    const email = cb.dataset.email
    const name = cb.dataset.name

    if (cb.checked) {
      this._distributeSelectedEmails.set(email, { name, email })
    } else {
      this._distributeSelectedEmails.delete(email)
    }

    this._updateDistributeUI()
  }

  toggleDistributeSelectAll(event) {
    const checked = event.currentTarget.checked
    for (const company of this._distributeMembers) {
      for (const user of company.users) {
        if (checked) {
          this._distributeSelectedEmails.set(user.email, { name: user.name, email: user.email })
        } else if (!this._distributeLockedEmails.has(user.email)) {
          this._distributeSelectedEmails.delete(user.email)
        }
      }
    }
    this._renderDistributeList()
    this._updateDistributeUI()
  }

  _updateDistributeUI() {
    const count = this._distributeSelectedEmails.size
    const totalUsers = this._distributeMembers.reduce((sum, c) => sum + c.users.length, 0)

    // Update send button
    this.distributeSendBtnTarget.disabled = count === 0

    // Update count label
    this.distributeCountTarget.textContent = count > 0 ? `(${count}/${totalUsers})` : ""

    // Update select-all checkbox state
    if (totalUsers > 0) {
      this.distributeSelectAllTarget.checked = count >= totalUsers
      this.distributeSelectAllTarget.indeterminate = count > 0 && count < totalUsers
    }
  }

  async submitDistribute() {
    const emails = [...this._distributeSelectedEmails.keys()]
    if (emails.length === 0) return

    this.distributeSendBtnTarget.disabled = true
    this.distributeSendBtnTarget.textContent = "Sending..."

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(this.distributeUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ emails, ids: this._distributeFileIds })
      })

      if (resp.ok) {
        this._closeDistributeModal()
        this._showToast("Notification sent successfully")
      } else {
        this._showToast("Failed to send notification", "error")
      }
    } catch {
      this._showToast("Failed to send notification", "error")
    } finally {
      this.distributeSendBtnTarget.disabled = false
      this.distributeSendBtnTarget.textContent = "Send Notification"
    }
  }

  skipDistribute() {
    this._closeDistributeModal()
  }

  _closeDistributeModal() {
    this.distributeModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._distributeFileIds = []
    this._distributeSelectedEmails = new Map()
    this._distributeLockedEmails = new Set()
  }

  _showToast(message, type = "success", dismissAfter = 5000) {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", String(dismissAfter))
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── Bluebeam Studio Sessions ──────────────────────────────────────
  // Open a spec section in Bluebeam (browser or Revu), work on it, then
  // finish the session choosing whether the marked-up file becomes a NEW
  // version or replaces the CURRENT version's file. Mirrors drawings.

  _bluebeamUrl(template, id) {
    return template.replace("__ID__", id)
  }

  _bluebeamEligible(file) {
    return file && file.is_pdf && !file.combined && file.status === "current"
  }

  // Row-menu entries for the file's current Bluebeam state.
  _bluebeamMenuItems(file) {
    if (!this._bluebeamEligible(file) || !this.hasBluebeamOpenUrlTemplateValue) return ""
    if (["creating", "finalizing", "syncing"].includes(file.bluebeam_status)) return ""

    if (file.bluebeam_status === "active") {
      return `
        <button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                data-action="click->specifications#openInBluebeam" data-file-id="${file.id}">Open in Bluebeam</button>
        <button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                data-action="click->specifications#showBluebeamFinishModal" data-file-id="${file.id}">Finish Bluebeam Session</button>
        <button type="button" class="block w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                data-action="click->specifications#showBluebeamDiscardModal" data-file-id="${file.id}">Discard Bluebeam Session</button>`
    }
    return `
      <button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
              data-action="click->specifications#openInBluebeam" data-file-id="${file.id}">Open in Bluebeam</button>`
  }

  // Inline status chip rendered next to the title.
  _bluebeamChip(file) {
    if (!this._bluebeamEligible(file)) return ""
    if (file.bluebeam_status === "active") {
      const action = this.bluebeamAllowedValue
        ? `onclick="event.stopPropagation()" data-action="click->specifications#showBluebeamFinishModal" data-file-id="${file.id}" title="Finish the Bluebeam session and save markups"`
        : `onclick="event.stopPropagation()" title="This document has an open Bluebeam session"`
      return `<button type="button" ${action}
        class="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer transition-colors align-middle">
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/></svg>
        In Bluebeam</button>`
    }
    if (["creating", "finalizing", "syncing"].includes(file.bluebeam_status)) {
      const label = file.bluebeam_status === "creating" ? "Opening in Bluebeam..." : "Saving from Bluebeam..."
      return `<span class="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium bg-blue-50 text-blue-500 align-middle">
        <svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
        ${label}</span>`
    }
    return ""
  }

  openInBluebeam(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    if (!id) return
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    this._triggerBluebeamOpen(id)
  }

  async _triggerBluebeamOpen(id) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(this._bluebeamUrl(this.bluebeamOpenUrlTemplateValue, id), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const data = await resp.json()

      if (data.needs_auth) {
        window.location.href = data.auth_url
        return
      }
      if (data.status === "active" && data.session_url) {
        this._bluebeamSessionUrl = data.session_url
        this._bluebeamSessionId = data.session_id
        this._setBluebeamRowStatus(id, "active")
        this._showBluebeamPopover()
        return
      }
      if (data.status === "creating") {
        this._setBluebeamRowStatus(id, "creating")
        this._pollBluebeamStatus(id)
      }
      if (data.error === "document_is_final") {
        this._showToast(data.message || "Document is final — unlock first", "error")
      }
    } catch (e) {
      console.error("[specifications] Bluebeam open error:", e)
      this._showToast("Failed to open in Bluebeam. Please try again.", "error")
    }
  }

  _showBluebeamPopover() {
    // Surface the Studio Session ID as a manual-join fallback: if the one-click
    // studio:// hand-off doesn't pull the file into Revu Desktop, the user can
    // paste this ID into Revu (Studio → Join) and the document loads.
    if (this.hasBluebeamSessionIdTarget) {
      this.bluebeamSessionIdTarget.textContent = this._bluebeamSessionId || "—"
    }
    if (this.hasBluebeamSessionIdCopyLabelTarget) {
      this.bluebeamSessionIdCopyLabelTarget.textContent = "Copy"
    }
    if (this.hasBluebeamPopoverTarget) this.bluebeamPopoverTarget.classList.remove("hidden")
  }

  // Bluebeam's own session launcher — the documented hand-off, which routes
  // to installed Revu (or explains itself when Revu is missing). The old
  // `studio://studio.bluebeam.com/sessions/…` scheme was invented: on Luma's
  // machine (2026-08-28) it fronted Revu but never loaded the session,
  // because Revu doesn't understand that path. Studio IDs are shown
  // dash-formatted (269-728-452), so a bare 9-digit id gets its dashes back.
  _bluebeamJoinUrl() {
    if (!this._bluebeamSessionId) return null
    const raw = String(this._bluebeamSessionId).trim()
    const id = /^\d{9}$/.test(raw) ? `${raw.slice(0, 3)}-${raw.slice(3, 6)}-${raw.slice(6)}` : raw
    return `https://studio.bluebeam.com/join.html?ID=${encodeURIComponent(id)}`
  }

  copyBluebeamSessionId() {
    if (!this._bluebeamSessionId) return
    navigator.clipboard.writeText(this._bluebeamSessionId).then(() => {
      if (this.hasBluebeamSessionIdCopyLabelTarget) {
        this.bluebeamSessionIdCopyLabelTarget.textContent = "Copied!"
        setTimeout(() => {
          if (this.hasBluebeamSessionIdCopyLabelTarget) this.bluebeamSessionIdCopyLabelTarget.textContent = "Copy"
        }, 2000)
      }
    })
  }

  closeBluebeamPopover() {
    if (this.hasBluebeamPopoverTarget) this.bluebeamPopoverTarget.classList.add("hidden")
  }

  launchBluebeamWeb() {
    if (this._bluebeamSessionUrl) window.open(this._bluebeamSessionUrl, "_blank")
    this.closeBluebeamPopover()
  }

  launchBluebeamDesktop() {
    const url = this._bluebeamJoinUrl()
    if (url) window.open(url, "_blank")
    this.closeBluebeamPopover()
  }

  copyBluebeamLink() {
    if (this._bluebeamSessionUrl) {
      navigator.clipboard.writeText(this._bluebeamSessionUrl).then(() => {
        if (this.hasBluebeamCopyLinkLabelTarget) {
          this.bluebeamCopyLinkLabelTarget.textContent = "Copied!"
          setTimeout(() => { this.bluebeamCopyLinkLabelTarget.textContent = "Copy Session Link" }, 2000)
        }
      })
    }
  }

  // Finish session — the user decides: new version or save into current.
  showBluebeamFinishModal(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    if (!id) return
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))

    this._bluebeamFinishId = id
    const file = this._filesData[String(id)]
    const currentRev = file?.revision_number ? `v${file.revision_number}` : "the current version"
    const nextRev = file?.revision_number ? `v${parseInt(file.revision_number, 10) + 1}` : "a new version"
    if (this.hasBluebeamFinishNextRevTarget) this.bluebeamFinishNextRevTarget.textContent = nextRev
    if (this.hasBluebeamFinishCurrentRevTarget) this.bluebeamFinishCurrentRevTarget.textContent = currentRev

    // Default back to "new version" each time
    this.bluebeamVersionRadioTargets.forEach(r => { r.checked = r.value === "new_version" })
    if (this.hasBluebeamFinishModalTarget) this.bluebeamFinishModalTarget.classList.remove("hidden")
  }

  closeBluebeamFinishModal() {
    if (this.hasBluebeamFinishModalTarget) this.bluebeamFinishModalTarget.classList.add("hidden")
    this._bluebeamFinishId = null
  }

  async confirmBluebeamFinish() {
    const id = this._bluebeamFinishId
    if (!id) return
    const mode = this.bluebeamVersionRadioTargets.find(r => r.checked)?.value || "new_version"
    this.closeBluebeamFinishModal()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(this._bluebeamUrl(this.bluebeamFinishUrlTemplateValue, id), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ version_mode: mode })
      })
      const data = await resp.json()
      if (data.status === "syncing") {
        this._bluebeamFinishMode = mode
        this._setBluebeamRowStatus(id, "syncing")
        this._pollBluebeamStatus(id)
      } else if (data.error) {
        this._showToast(data.error, "error")
      }
    } catch (e) {
      console.error("[specifications] Bluebeam finish error:", e)
      this._showToast("Failed to save markups. Please try again.", "error")
    }
  }

  showBluebeamDiscardModal(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    if (!id) return
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    this._bluebeamDiscardId = id
    if (this.hasBluebeamDiscardModalTarget) this.bluebeamDiscardModalTarget.classList.remove("hidden")
  }

  closeBluebeamDiscardModal() {
    if (this.hasBluebeamDiscardModalTarget) this.bluebeamDiscardModalTarget.classList.add("hidden")
    this._bluebeamDiscardId = null
  }

  async confirmBluebeamDiscard() {
    const id = this._bluebeamDiscardId
    if (!id) return
    this.closeBluebeamDiscardModal()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch(this._bluebeamUrl(this.bluebeamCloseUrlTemplateValue, id), {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      this._setBluebeamRowStatus(id, null)
      this._showToast("Bluebeam session discarded")
    } catch (e) {
      console.error("[specifications] Bluebeam discard error:", e)
      this._showToast("Failed to discard session", "error")
    }
  }

  _setBluebeamRowStatus(id, status) {
    const file = this._filesData[String(id)]
    if (!file) return
    file.bluebeam_status = status
    if (this.currentViewValue === "list") this._reRenderTable()
  }

  _pollBluebeamStatus(id) {
    if (this._bluebeamPollers[id]) clearInterval(this._bluebeamPollers[id])

    const url = this._bluebeamUrl(this.bluebeamStatusUrlTemplateValue, id)
    let pollCount = 0
    const maxPolls = 40 // 40 x 3s = 2 minutes
    const stop = () => { clearInterval(this._bluebeamPollers[id]); delete this._bluebeamPollers[id] }

    this._bluebeamPollers[id] = setInterval(async () => {
      pollCount++
      try {
        const resp = await fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
        const data = await resp.json()

        if (data.status === "active" && data.session_url) {
          stop()
          this._bluebeamSessionUrl = data.session_url
          this._bluebeamSessionId = data.session_id
          this._setBluebeamRowStatus(id, "active")
          this._showBluebeamPopover()
        } else if (data.status === "closed") {
          stop()
          const mode = this._bluebeamFinishMode || data.version_mode
          if (mode === "current_version") {
            this._showToast("Bluebeam markups saved to the current version")
          } else if (mode === "new_version") {
            this._showToast("Bluebeam markups saved — new version created")
          }
          this._bluebeamFinishMode = null
          this._resetAndLoad()
        } else if (data.status === "error") {
          stop()
          this._setBluebeamRowStatus(id, null)
          // A reason from the server (e.g. no Bluebeam licence) is long: leave it up to read.
          this._showToast(data.message || "Bluebeam session failed. Please try again.", "error", data.message ? 15000 : 5000)
        } else if (pollCount >= maxPolls) {
          stop()
          this._showToast("Bluebeam is taking longer than expected — check back shortly.", "error")
        }
        // "none"/"creating"/"finalizing"/"syncing" — keep polling
      } catch (e) {
        console.error("[specifications] Bluebeam poll error:", e)
        stop()
      }
    }, 3000)
  }

  // ── Private: File Type Icons ──────────────────────────────────────

  _fileTypeIcon(ext, sizeClass = "w-5 h-5") {
    ext = (ext || "").toLowerCase()

    if (ext === "pdf") {
      return `<svg class="${sizeClass} text-red-500" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
    }
    if (["jpg","jpeg","gif","png","tiff","bmp","webp","heic","heif"].includes(ext)) {
      return `<svg class="${sizeClass} text-emerald-500" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z" clip-rule="evenodd"/></svg>`
    }
    if (["xls","xlsx","xlsm"].includes(ext)) {
      return `<svg class="${sizeClass} text-green-600" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
    }
    if (["doc","docx","txt"].includes(ext)) {
      return `<svg class="${sizeClass} text-blue-500" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
    }
    if (ext === "dwg") {
      return `<svg class="${sizeClass} text-purple-500" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
    }
    if (["zip","rar"].includes(ext)) {
      return `<svg class="${sizeClass} text-yellow-600" viewBox="0 0 24 24" fill="currentColor"><path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z"/></svg>`
    }
    if (["ppt","pptx"].includes(ext)) {
      return `<svg class="${sizeClass} text-orange-500" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
    }
    // Generic
    return `<svg class="${sizeClass} text-gray-400" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
  }

  // Purple pulse on a row an AI answer cited: scroll it into view and hold the
  // mark long enough to be seen (Phelipe 2026-08-21). Purple is the AI colour
  // everywhere else in the app, so it reads as "the assistant pointed here".
  _markCitedRow(fileId) {
    const paint = () => {
      const row = this.element.querySelector(`tr[data-file-id="${fileId}"], [data-card-file-id="${fileId}"]`)
      if (!row) return false
      row.scrollIntoView({ behavior: "smooth", block: "center" })
      row.classList.add("ring-2", "ring-purple-500", "ring-inset", "bg-purple-50")
      setTimeout(() => row.classList.remove("ring-2", "ring-purple-500", "ring-inset", "bg-purple-50"), 6000)
      return true
    }
    // The row may still be rendering when the deep link lands.
    if (!paint()) setTimeout(paint, 400)
  }

}
